/**
 * RaceTrace Live: Strategy. From the live chart alone (laps, pit markers, cautions, time to go),
 * for each car of a class:
 *
 *   - stops still to make, from the long stints the class has run so far (fuel and driver time
 *     make a stint, and the cars of a class run near the same length), a last stint running a
 *     little longer by saving fuel (canStretch: at most SAVE_LAPS, MAX_SAVE_SHARE of a lap's fuel);
 *   - the order after those stops ("net"), each car's gap to the class leader plus a green stop's
 *     time lost for each stop it still owes, less the leader's;
 *   - a projected finish, the net gaps moved on at each car's green pace to the flag;
 *
 * and for the focus car its pit window (the earliest lap to stop and still make no more stops
 * than now, the last lap its longest stint reaches) and where it rejoins if it stops now, with
 * what a stop under the current caution saves.
 *
 * Every figure is an estimate from this race's own stints and stops; it moves with each update.
 * The times are each car's summed lap times (the chart's own convention, as the Gaps panel).
 *
 * The race's stops (raceStops) depend on the chart alone, and measuring a caution stop scans every
 * lap of the race, so they are worked out once per chart version (the live page swaps in new chart
 * objects on each update) and shared by every tab that asks: the Strategy tab each second, the
 * map's pit ghost. The pit window and "if it stops now" are worked out for any car of the class
 * (pitWindowFor, pitNowFor), not only the focus car.
 *
 * Two different questions use two different measures, and they are named so:
 *   - "rejoins P5 in class" (pitNowFor): the class's gaps at the line, from summed lap times;
 *   - "traffic at rejoin" (pit-ghost.ts, ghost-traffic.ts): the ghost's place on the road, all classes.
 */
import type { AnnotationData, CarData, PitMarker, RaceChartData } from "@shared/types";
import { isGreenFlag } from "./flags";
import { MIN_PACE_LAPS, PACE_LAPS } from "./live-gaps";
import { LONG_STOP_S } from "./long-stops";
import { lapsLeftByCar, lapsToGo, overallLeaderKey } from "../live/race-laps";

type Lap = RaceChartData["cars"][string]["laps"][number];

/** A stint counts towards the class's stint length when it is at least this share of the longest (itself corroborated by one within this share). */
export const STINT_SHARE = 0.8;
/**
 * Saving fuel: a last stint can run at most this many laps longer than the car's stint cap (the
 * class's stint length, or its own longest when longer): a lap or two is what a team can save, more
 * is not a plan. Past that the car owes a stop (a splash).
 */
export const SAVE_LAPS = 2;
/**
 * And it can save at most this share of a lap's fuel on each lap it has left: lift and coast saves
 * "five to ten percent" (iRacerHUB, GT3); more than that from a few laps is not a plan either.
 */
export const MAX_SAVE_SHARE = 0.1;
/**
 * Fuel saved for lap time given up: each 1 % slower lap saves this many % of a lap's fuel. Taken
 * below what is published (lift and coast: 10 % of the fuel for about 0.5 % of the lap at
 * Barcelona, fastest-lap's optimal laps; 5-10 % for two or three tenths in a GT3, iRacerHUB; 20 %
 * for half a second short-shifting, GTPlanet): those are 20 to 40 times, from the quickest drivers
 * or simulators, alone on track. And below what WRL GTO showed: Sunday 7 Hour 2026, #120 ran its
 * last stint 30 laps on fuel for 28 (7 % a lap) at a median 139.65 s against 138.98 s the stint
 * before (0.5 %): 14 or more, and won by not making the splash #119 made. 10, half the published
 * figures' least, allows for traffic and drivers saving fuel for the first time.
 */
export const FUEL_PER_TIME = 10;

/**
 * Whether `laps` laps can be run on `fuelLaps` laps of fuel (at the car's pace): with fuel to
 * spare, or by saving at most SAVE_LAPS of it, and no more than MAX_SAVE_SHARE of a lap's fuel on
 * each lap.
 */
export function canStretch(fuelLaps: number, laps: number): boolean {
  if (laps <= fuelLaps) return true;
  return laps - fuelLaps <= SAVE_LAPS && fuelLaps >= laps * (1 - MAX_SAVE_SHARE) - 1e-9;
}

/**
 * Stops a car still needs: none when this stint, saving what it can from its stop, reaches the
 * flag (timing can not show fuel saved, so the stint is judged whole: reachToFlag); else enough
 * full stints that the last, saving what it can, does. `stintFuel`: the fuel used since its stop,
 * in green laps; `lapsLeft`: the laps it still runs.
 */
export function stopsFor(cap: number, stintFuel: number, lapsLeft: number): number {
  return canStretch(cap, stintFuel + lapsLeft) ? 0 : Math.max(1, Math.ceil((stintFuel + lapsLeft - lastStintMax(cap)) / cap - 1e-9));
}

/** The most laps of fuel (from the stint it is on, in green laps) `stops` stops give: full tanks, the last stretched (lastStintMax). */
export function fuelWithStops(cap: number, stops: number): number {
  return stops * cap + lastStintMax(cap);
}

/**
 * A stop count on the edge: one lap more to the flag would need a stop more (`more`: what its stops
 * give, just enough), or one lap fewer a stop fewer (`fewer`: what a stop fewer gives, just short);
 * the race's laps to go is an estimate, so the count could go either way. With the fuel it needs
 * to the flag (from its last stop, in green laps). Null when neither.
 */
/** The laps a car's fuel is counted against (fuelLapsLeft; its laps to go when not set). */
export const fuelLaps = (c: Pick<CarStrategy, "fuelLapsLeft" | "lapsLeft">): number | null => c.fuelLapsLeft ?? c.lapsLeft;

export function stopsEdge(c: CarStrategy): { need: number; stops: number; more: { gives: number } | null; fewer: { gives: number } | null } | null {
  const left = fuelLaps(c);
  if (c.stintCap === null || left === null || c.stopsToGo === null || c.stopped) return null;
  const more = stopsFor(c.stintCap, c.stintFuel, left + 1) > c.stopsToGo;
  const fewer = c.stopsToGo > 0 && left > 0 && stopsFor(c.stintCap, c.stintFuel, left - 1) < c.stopsToGo;
  if (!more && !fewer) return null;
  return {
    need: Math.round((c.stintFuel + left) * 10) / 10,
    stops: c.stopsToGo,
    more: more ? { gives: fuelWithStops(c.stintCap, c.stopsToGo) } : null,
    fewer: fewer ? { gives: fuelWithStops(c.stintCap, c.stopsToGo - 1) } : null,
  };
}

/** A projected finish this near another car (s a lap to go, at least CLOSE_MIN_S) is too close to call. */
export const CLOSE_PER_LAP_S = 0.3;
export const CLOSE_MIN_S = 5;

/**
 * Too close to call: the car projected just ahead of or behind it within CLOSE_PER_LAP_S a lap
 * to go (at least CLOSE_MIN_S); a few tenths a lap of pace, a stop's time or traffic swaps them.
 * The nearer of the two, the gap and the margin used. Null when neither is that near.
 */
export function closeCall(s: Pick<LiveStrategy, "cars">, c: CarStrategy): { other: CarStrategy; gapS: number; closeS: number } | null {
  if (c.projPos === null || c.projGapS === null) return null;
  const closeS = Math.max(CLOSE_MIN_S, CLOSE_PER_LAP_S * (c.lapsLeft ?? 0));
  const near = s.cars
    .filter((o) => o !== c && o.projPos !== null && o.projGapS !== null && Math.abs(o.projPos - c.projPos!) === 1)
    .map((o) => ({ other: o, gapS: Math.abs(o.projGapS! - c.projGapS!) }))
    .sort((a, b) => a.gapS - b.gapS)[0];
  return near && near.gapS < closeS ? { ...near, closeS } : null;
}

/** The longest last stint a full stint's fuel (`cap` laps) runs to, saving fuel (canStretch). */
export function lastStintMax(cap: number): number {
  return Math.min(cap + SAVE_LAPS, Math.floor(cap / (1 - MAX_SAVE_SHARE) + 1e-9));
}
/**
 * A lap under a caution (a full-course yellow, Code 35/60) uses this share of a green lap's fuel.
 * Stints and the fuel a car has used are counted in green laps' fuel with it; the laps to the
 * flag are taken as green (the next caution is not known), so a caution still to come only leaves
 * more fuel than the figures say.
 */
export const CAUTION_FUEL = 0.5;

/**
 * Cautions still to come, allowed for on average: how often one starts, per hour of green running,
 * in each phase of a race, and how long one lasts. Each series has its own (cautionRatesFor): rates
 * from one series put on another miss the time under caution by 15 to 18 points of the time left.
 * WRL from its 2026 races (Eagles Canyon, Barber, Thunderhill, Road America, Daytona, Road Atlanta,
 * Watkins Glen and Utah Saturday; 78 cautions); IMSA WeatherTech from 10 of its 2026 races (47
 * cautions) and the Pilot Challenge from 10 (30). In each: more at the start and after a restart,
 * fewer in the last hour. A caution's length is averaged with any single caution taken as at most
 * an hour (Daytona 2026's 6.5-hour caution for fog is weather, not racing). A race's own cautions so
 * far say little about its next ones (back-tested: weighting them in did not help), so they are not used.
 */
export interface CautionRates {
  /** Cautions per green hour in the first START_PHASE_S of the race. */
  startPerH: number;
  /** In the RESTART_PHASE_S after a caution ends. */
  restartPerH: number;
  /** In the last LAST_PHASE_S. */
  lastHourPerH: number;
  /** The rest of the race. */
  middlePerH: number;
  /** A caution's average length (min). */
  meanMin: number;
  /**
   * In the last hour, the stops a car still makes follow its stop count against its class's, not
   * the fuel count alone (lateStopsToGo): IMSA, where the stops are placed by drive time and the
   * team's plan and the tank is rarely run out. Off for WRL, where the fuel count holds.
   */
  lateStopsByClass?: boolean;
  /** Whether a car stops when a caution comes (cautionStopChance); without it, no stop under a caution is counted. */
  stopPolicy?: CautionStopPolicy;
  /**
   * IMSA's short full-course yellow (isShortCaution): the pit lane stays closed until the field
   * comes to the restart, so no car stops under it.
   */
  shortCautionPitsClosed?: boolean;
  /**
   * The share of the projection taken in the last hour (projectionShare; all of it when not set).
   * WeatherTech 0.25: there the stops still owed and the paces moved the order away from the finish
   * even in the last hour (2026 development races, class place within one over the last hour:
   * today's gaps 67.3%, the full projection 56.8%, a quarter of it 62.9%): its cars stretch their
   * last stint further than the fuel count allows, so a stop is often counted that is not made.
   */
  projectionNearShare?: number;
  /** How long the series' cautions last: their 5th, 10th, … 95th percentiles (min), for cautionLengthNote. */
  lengthPercentilesMin?: number[];
  /**
   * The chance of another caution before the flag (cautionChance): the expected cautions to come
   * times chanceScale, and never under chanceMin. Without chanceScale the tab gives no chance.
   */
  chanceScale?: number;
  chanceMin?: number;
  /** Hours of green running per caution in the series' 2026 races, for the Cautions card. */
  greenHoursPerCaution?: number;
}

export type CautionWord = "likely" | "about even" | "unlikely";
export const CAUTION_LIKELY = 0.65;
export const CAUTION_UNLIKELY = 0.35;

/**
 * The chance that another caution starts before the flag. The expected number to come: for each
 * minute from now (under a caution, from when half of those this long are over: cautionLengthNote)
 * to the flag, the phase's rate (start, restart, last hour, middle) over the minute's green share,
 * summed; the chance is 1 - e^(-scale x that), and never under the series' minimum.
 * Fitted on the series' 2026 races, one race left out at a time. WRL (scale 0.62): of the calls
 * "likely", 83% had another caution; "about even" 57%; "unlikely" 19%. WeatherTech (scale 1): 91%
 * and 57%; late cautions came about half the time there, so it never says "unlikely" (minimum
 * 0.4). Null without a time to go or the series' fit.
 */
export function cautionChance(
  rates: CautionRates,
  o: Pick<CautionOutlook, "nowS" | "lastEndS" | "cautionStartS">,
  timeToGoS: number | null,
): { chance: number; word: CautionWord } | null {
  if (rates.chanceScale === undefined || timeToGoS === null || !(timeToGoS > 0)) return null;
  const D = rates.meanMin * 60;
  const held = (perH: number) => ((perH / 3600) * D) / (1 + (perH / 3600) * D);
  const flag = o.nowS + timeToGoS;
  const under = o.cautionStartS !== null;
  const soFarMin = under ? Math.max(0, o.nowS - o.cautionStartS!) / 60 : 0;
  const more = under ? (cautionLengthNote(rates, soFarMin)?.halfMoreMin ?? Math.max(1, rates.meanMin - soFarMin)) * 60 : 0;
  const from = o.nowS + more;
  const lastEnd = under ? from : o.lastEndS;
  let expected = 0;
  for (let x = from; x < flag; x += 60) {
    const perH =
      x < START_PHASE_S ? rates.startPerH
      : lastEnd !== null && x - lastEnd < RESTART_PHASE_S ? rates.restartPerH
      : flag - x <= LAST_PHASE_S ? rates.lastHourPerH
      : rates.middlePerH;
    expected += (perH / 60) * (1 - held(perH));
  }
  const chance = Math.max(rates.chanceMin ?? 0, 1 - Math.exp(-rates.chanceScale * expected));
  return { chance, word: chance >= CAUTION_LIKELY ? "likely" : chance < CAUTION_UNLIKELY ? "unlikely" : "about even" };
}

/**
 * How long a caution under way is likely to last, from the series' cautions (lengthPercentilesMin,
 * the 2026 races): the usual length, the length 9 in 10 are over by, and, this far in, the time
 * within which half of those that last this long are over. Null without the series' lengths.
 */
export function cautionLengthNote(rates: CautionRates, soFarMin: number): { medianMin: number; p90Min: number; halfMoreMin: number | null } | null {
  const q = rates.lengthPercentilesMin;
  if (!q || q.length !== 19) return null;
  // Of the percentile points still to come, the middle one: half of the cautions this long end by then.
  const later = q.filter((x) => x > soFarMin);
  const halfMoreMin = later.length ? Math.max(1, Math.round(later[Math.floor((later.length - 1) / 2)] - soFarMin)) : null;
  return { medianMin: Math.round(q[9]), p90Min: Math.round(q[17]), halfMoreMin };
}

/**
 * IMSA 2026 (WeatherTech and Pilot Challenge): a full-course yellow that starts within 30 minutes of
 * the start or 15 minutes of a green restart, or in the last 30 minutes, is a "short" one: the pit
 * lane stays closed until the field comes to the restart (IMSA may make it a standard one, pits
 * open, once it passes 15 minutes). In the 2026 WeatherTech races 4% of cars stopped under one,
 * against 81% under a standard one.
 */
export const SHORT_CAUTION_START_S = 1800;
export const SHORT_CAUTION_RESTART_S = 900;
export const SHORT_CAUTION_LAST_S = 1800;
export function isShortCaution(startS: number, lastEndS: number | null, toGoS: number): boolean {
  return startS < SHORT_CAUTION_START_S || (lastEndS !== null && startS - lastEndS < SHORT_CAUTION_RESTART_S) || toGoS <= SHORT_CAUTION_LAST_S;
}

/** The pit-lane category race control opens a class in (IMSA): prototypes, GT, or Pilot Challenge's GS and TCR. */
export function pitCategory(cls: string): "prototype" | "gt" | "gs" | "tcr" | null {
  const c = cls.trim().toUpperCase();
  if (/^(GTP|LMP\d?|DPI|P\d)$/.test(c)) return "prototype";
  if (/^(GTD( PRO)?|GTLM|GT\d?|GT)$/.test(c)) return "gt";
  if (c === "GS") return "gs";
  if (c === "TCR") return "tcr";
  return null;
}

/**
 * The pit lane under the caution under way, from race control's calls (data.pitLane, IMSA): the
 * pit lane closes when a full-course yellow comes and opens by category ("PITS OPEN PROTOTYPE",
 * then GT, then all) unless race control calls a "SHORT YELLOW" (closed until the restart). On the
 * 2026 IMSA development races race control's call and the timing rule (isShortCaution) agreed on
 * 70 of 74 yellows; the 4 others the rule took for short, race control ran as standard ones.
 * Null when the timing has no race control messages, or on a green track.
 */
export function pitLaneNow(data: RaceChartData, cls: string, caution: boolean): { short: boolean; open: boolean; all: boolean } | null {
  if (!data.pitLane || !caution) return null;
  // From the latest full-course yellow on (the one under way).
  let from = -1;
  data.pitLane.forEach((x, i) => {
    if (x.state === "yellow") from = i;
  });
  if (from < 0) return null;
  const calls = data.pitLane.slice(from + 1);
  const cat = pitCategory(cls);
  let open = false;
  let all = false;
  for (const x of calls) {
    if (x.state === "closed" || x.state === "short") open = all = false;
    else if (x.state === "open_all" || (cat !== null && x.state === `open_${cat}`)) {
      open = true;
      all = x.state === "open_all";
    }
  }
  return { short: calls.some((x) => x.state === "short"), open, all };
}

/**
 * The pit lane for a class under the caution under way: race control's calls when the timing has
 * them (pitLaneNow); else, where the series closes the pit lane on a short yellow
 * (shortCautionPitsClosed, IMSA), closed on a short one by the timing rule (isShortCaution). Null on
 * a green track, or when it is not known (a standard yellow without race control's calls, or a
 * series without the rule). The Strategy tab and the map's pit light use the same.
 */
export function pitLaneState(
  data: RaceChartData,
  cls: string,
  caution: boolean,
  timeToGoS: number | null,
  rates: CautionRates,
  outlook?: Pick<CautionOutlook, "nowS" | "lastEndS" | "cautionStartS">
): { open: boolean; short: boolean; fromRc: boolean; all?: boolean } | null {
  if (!caution) return null;
  const rc = pitLaneNow(data, cls, caution);
  if (rc) return { open: rc.open, short: rc.short, fromRc: true, all: rc.all };
  if (!rates.shortCautionPitsClosed) return null;
  const o = outlook ?? cautionOutlook(data, timeToGoS, rates);
  if (o.cautionStartS === null) return null;
  const short = isShortCaution(o.cautionStartS, o.lastEndS, timeToGoS !== null ? timeToGoS + (o.nowS - o.cautionStartS) : Infinity);
  return short ? { open: false, short: true, fromRc: false } : null;
}

/**
 * When a caution comes, how likely a car is to stop under it: a logistic curve, log-odds = base
 * + perFuelShare × (fuel used since its last stop over the class's stint) + canReachFlag (when the
 * fuel it has left reaches the flag) + lastHour (in the race's last hour). Fitted on every car
 * running at each caution of the series' 2026 races (WRL: 1,471 car-cautions, 56 cautions;
 * WeatherTech: 1,556, 35): about a third stop with a fifth of their fuel used, nine in ten with
 * four fifths; few stop that can reach the flag, or in WeatherTech's last hour. Left out race by
 * race, it ranks who stops at AUC 0.73 (WRL) and 0.79 (WeatherTech), and its chances match what
 * happened within 5 points in each fifth.
 */
export interface CautionStopPolicy {
  base: number;
  perFuelShare: number;
  canReachFlag: number;
  lastHour: number;
}
export const WRL_CAUTION_STOPS: CautionStopPolicy = { base: -0.81, perFuelShare: 2.91, canReachFlag: -0.37, lastHour: -0.59 };
// WeatherTech: fitted on its standard cautions only (1,195 car-cautions); the short ones are the rule's (isShortCaution).
export const WEATHERTECH_CAUTION_STOPS: CautionStopPolicy = { base: -0.09, perFuelShare: 6.1, canReachFlag: -0.93, lastHour: -2.3 };
/** Both series together: for a series without its own (the Pilot Challenge's races are not studied yet). */
export const POOLED_CAUTION_STOPS: CautionStopPolicy = { base: -0.71, perFuelShare: 3.41, canReachFlag: -0.27, lastHour: -2.09 };

// Caution lengths (min), 5th to 95th percentile: WRL 78 cautions in 14 races, WeatherTech 47 in 10,
// Pilot Challenge 28 in 9 (2026; Road Atlanta left out).
export const WRL_CAUTION_LENGTHS = [0.5, 3.9, 5.2, 6.3, 6.8, 7.2, 7.8, 8.4, 9.0, 9.3, 9.7, 10.3, 10.8, 11.1, 11.9, 12.9, 14.7, 16.7, 19.4];
export const WEATHERTECH_CAUTION_LENGTHS = [4.5, 5.1, 6.4, 7.2, 8.8, 10.6, 11.8, 14.1, 14.5, 15.2, 16.4, 17.1, 18.0, 18.4, 20.6, 20.9, 21.2, 22.0, 25.4];
export const PILOT_CHALLENGE_CAUTION_LENGTHS = [4.5, 5.3, 5.7, 6.4, 6.9, 7.5, 8.2, 8.3, 9.8, 11.1, 11.9, 12.3, 12.8, 13.2, 13.6, 14.4, 14.8, 16.9, 25.2];
// Green running per caution, 2026: WRL 59 cautions in 99.8 green hours (14 races), WeatherTech 50 in 43.7 (10).
export const WRL_CAUTION_RATES: CautionRates = { startPerH: 0.96, restartPerH: 0.98, lastHourPerH: 0.5, middlePerH: 0.77, meanMin: 9.9, stopPolicy: WRL_CAUTION_STOPS, lengthPercentilesMin: WRL_CAUTION_LENGTHS, chanceScale: 0.62, greenHoursPerCaution: 1.69 };
export const WEATHERTECH_CAUTION_RATES: CautionRates = { lateStopsByClass: true, startPerH: 1.44, restartPerH: 1.47, lastHourPerH: 0.72, middlePerH: 0.95, meanMin: 15.4, stopPolicy: WEATHERTECH_CAUTION_STOPS, shortCautionPitsClosed: true, projectionNearShare: 0.25, lengthPercentilesMin: WEATHERTECH_CAUTION_LENGTHS, chanceScale: 1, chanceMin: 0.4, greenHoursPerCaution: 0.87 };
export const PILOT_CHALLENGE_CAUTION_RATES: CautionRates = { lateStopsByClass: true, startPerH: 1.72, restartPerH: 1.97, lastHourPerH: 0.9, middlePerH: 2.38, meanMin: 11.2, stopPolicy: POOLED_CAUTION_STOPS, shortCautionPitsClosed: true, lengthPercentilesMin: PILOT_CHALLENGE_CAUTION_LENGTHS };

/** The chance a car stops under a caution that comes now (CautionStopPolicy). */
export function cautionStopChance(p: CautionStopPolicy, fuelShare: number, canReachFlag: boolean, lastHour: boolean): number {
  const z = p.base + p.perFuelShare * Math.min(1.5, Math.max(0, fuelShare)) + (canReachFlag ? p.canReachFlag : 0) + (lastHour ? p.lastHour : 0);
  return 1 / (1 + Math.exp(-z));
}

/**
 * The caution rates for a race's series: its series and sub-series names ("IMSA" and "WeatherTech",
 * or a full name such as "IMSA WeatherTech SportsCar Championship"). A series without rates of its
 * own gets WRL's until it has races to fit them on.
 */
export function cautionRatesFor(series: string | null | undefined, subSeries?: string | null): CautionRates {
  const s = `${series ?? ""} ${subSeries ?? ""}`.toLowerCase();
  if (s.includes("weathertech")) return WEATHERTECH_CAUTION_RATES;
  if (s.includes("pilot challenge")) return PILOT_CHALLENGE_CAUTION_RATES;
  return WRL_CAUTION_RATES;
}

/** No cautions to come: every lap to the flag green. */
export const NO_CAUTION_RATES: CautionRates = { startPerH: 0, restartPerH: 0, lastHourPerH: 0, middlePerH: 0, meanMin: 0 };
export const START_PHASE_S = 1800;
export const RESTART_PHASE_S = 900;
export const LAST_PHASE_S = 3600;
/** The last hour's stop rule (lateStopsToGo): no stop in the last LATE_NO_STOP_S; from there to LATE_BEHIND_ONLY_S, only a car behind its class's stop count. */
export const LATE_NO_STOP_S = 1200;
export const LATE_BEHIND_ONLY_S = 2400;

/**
 * The stops a car still makes in the last hour of an IMSA race (CautionRates.lateStopsByClass),
 * from the fuel count and its stop count against the class's median. The 2026 WeatherTech and
 * Pilot Challenge races, every car the fuel count said was short of the flag: in the last 20
 * minutes 5-28 % stopped again (the model said 80-90 %); at 20-40 minutes to go 44-52 % of those
 * a stop behind their class did, 10-39 % of the rest; at 40-60 minutes 79-89 % of those behind,
 * 48-59 % of those level, and in the Pilot Challenge 37 % of those a stop ahead (72 % in
 * WeatherTech). Teams stretch, save, and plan by drive time; the tank is rarely run out. So:
 * under 20 minutes, no stop; 20-40, the fuel count only for a car behind its class; 40-60, the
 * fuel count, except a Pilot Challenge car a stop ahead of its class; beyond, the fuel count.
 * Null (no change) without a time to go, a stop count, or outside the last hour.
 */
export function lateStopsToGo(fuelStops: number | null, madeVsClass: number, timeToGoS: number | null, sprint: boolean): number | null {
  if (fuelStops === null || timeToGoS === null || timeToGoS >= LAST_PHASE_S || fuelStops <= 0) return null;
  if (timeToGoS < LATE_NO_STOP_S) return 0;
  if (timeToGoS < LATE_BEHIND_ONLY_S) return madeVsClass <= -1 ? fuelStops : 0;
  return sprint && madeVsClass >= 1 ? 0 : fuelStops;
}
/** A caution lap's time over a green lap's, until the race has CAUTION_RATIO_LAPS of its own (Barber 2026: 2.4; Utah: 1.6). */
export const CAUTION_LAP_RATIO = 2;
export const CAUTION_RATIO_LAPS = 3;

export interface CautionOutlook {
  /** Share of the race time still to run expected under a caution. */
  share: number;
  /** A caution lap's time over a green lap's. */
  lapRatio: number;
  /** Time over green pace of an average lap to come (cautions in it): laps to go are the time to go over pace × this. */
  paceFactor: number;
  /** Fuel of an average lap to come, in green laps (a caution lap uses CAUTION_FUEL of one). */
  fuelFactor: number;
  /** Race time now (s): the overall leader's laps summed. */
  nowS: number;
  /** Race time the last caution ended (s); null before any. */
  lastEndS: number | null;
  /** Race time the caution under way began (s): the start of the leader's first lap in it; null on a green track. */
  cautionStartS: number | null;
  /** Cautions so far (bands of the leader's laps under a caution) and the time under them (s). */
  count: number;
  yellowS: number;
}

/**
 * The cautions still to come, on average: for each minute to the flag, the share of time a caution
 * holds at that phase's rate (rate × length over 1 + rate × length), averaged; and how much slower a
 * caution lap is (the overall leader's, once it has run CAUTION_RATIO_LAPS of them). The race time
 * now is the overall leader's race clock (raceClock); the phase after a restart counts from the end of the
 * last caution band. Without a time to go, the middle of the race's rate throughout.
 */
export function cautionOutlook(data: RaceChartData, timeToGoS: number | null, rates: CautionRates = WRL_CAUTION_RATES): CautionOutlook {
  const k = overallLeaderKey(data);
  const laps = k ? data.cars[k].laps : [];
  const fcy = fcySet(data);
  // The leader's race clock: the timing's race time where the chart has it (elapsedAt), else its
  // lap times added up. Summed lap times leave out a red flag's stoppage and drift (WRL 2026: 26 s
  // to 80 min by the flag), which moved the phases and the last caution's end with them.
  const clock = k ? elapsedAt(laps) : new Map<number, number>();
  let now = 0;
  let lastEnd: number | null = null;
  let start: number | null = null;
  let count = 0;
  let yellowS = 0;
  const slow: number[] = [];
  for (let i = 0; i < laps.length; i++) {
    const l = laps[i];
    const before = now;
    now = clock.get(l.l) ?? now + (l.ltSec > 0 ? l.ltSec : 0);
    const yellow = fcy.has(l.l) || (!!l.flag && !isGreenFlag(l.flag));
    if (yellow && start === null) {
      start = before;
      count++;
    }
    if (!yellow) start = null;
    if (yellow) {
      yellowS += l.ltSec > 0 ? l.ltSec : 0;
      if (l.pit === 0) slow.push(l.ltSec);
      const next = laps[i + 1];
      if (next && !(fcy.has(next.l) || (!!next.flag && !isGreenFlag(next.flag)))) lastEnd = now;
    }
  }
  const D = rates.meanMin * 60;
  const held = (perH: number) => (perH / 3600) * D / (1 + (perH / 3600) * D);
  let share: number;
  if (timeToGoS === null || timeToGoS <= 0) share = held(rates.middlePerH);
  else {
    let sum = 0;
    let n = 0;
    for (let x = 0; x < timeToGoS; x += 60) {
      const at = now + x;
      const perH =
        at < START_PHASE_S ? rates.startPerH
        : lastEnd !== null && at - lastEnd < RESTART_PHASE_S ? rates.restartPerH
        : timeToGoS - x <= LAST_PHASE_S ? rates.lastHourPerH
        : rates.middlePerH;
      sum += held(perH);
      n++;
    }
    share = n ? sum / n : 0;
  }
  const green = k ? greenPace(laps, data, fcy, PROJECTION_LAPS) : null;
  const measured = green && slow.length >= CAUTION_RATIO_LAPS ? median(slow)! / green : null;
  const lapRatio = Math.min(4, Math.max(1.2, measured ?? CAUTION_LAP_RATIO));
  const perGreen = 1 - share + share / lapRatio;
  const lapShare = share / lapRatio / perGreen;
  return { share, lapRatio, paceFactor: 1 / perGreen, fuelFactor: 1 - lapShare * (1 - CAUTION_FUEL), nowS: now, lastEndS: lastEnd, cautionStartS: start, count, yellowS };
}

/** A caution's start rate (per hour of green running) at race time `at`, as cautionOutlook reads it. */
function phaseRatePerH(rates: CautionRates, at: number, lastEnd: number | null, toGoS: number): number {
  return at < START_PHASE_S ? rates.startPerH
    : lastEnd !== null && at - lastEnd < RESTART_PHASE_S ? rates.restartPerH
    : toGoS <= LAST_PHASE_S ? rates.lastHourPerH
    : rates.middlePerH;
}

/** Runs of the stop simulation per car (stopsWithCautions). */
export const CAUTION_STOP_RUNS = 400;

/** A small seeded random generator (mulberry32), so a car's figures do not change between redraws. */
function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface CautionStopsInput {
  /** Green laps its fuel runs (stintCap), and the class's stint (what fuel used is measured against). */
  cap: number;
  classStint: number;
  /** Fuel used since its last stop (green laps). */
  stintFuel: number;
  /** Laps it still runs to the flag. */
  lapsLeft: number;
  /** Its green lap (s). */
  lapS: number;
  timeToGoS: number;
  rates: CautionRates;
  outlook: Pick<CautionOutlook, "lapRatio" | "nowS" | "lastEndS"> & { cautionStartS?: number | null };
  /** Under a caution now, and whether it has stopped in it already. */
  underCaution: boolean;
  /** Race control called the caution under way a short one (pitLaneNow); null to go by the timing rule. */
  cautionShort?: boolean | null;
  stoppedThisCaution: boolean;
  seed: number;
}

/**
 * Stops to go with the cautions still to come: the car's laps to the flag run CAUTION_STOP_RUNS
 * times, cautions coming at the series' rate for each phase of the race and lasting its average
 * length (exponentially), a caution lap taking CAUTION_FUEL of a green lap's fuel. When one comes,
 * the car stops with cautionStopChance; under green it stops only when its fuel would not reach
 * the flag and it cannot run another lap (the last stint stretched as stopsFor allows). The stops
 * it most often makes, how often each count came up (share of runs, by count), and how many of its
 * stops on average were made because a caution came (`underCaution`).
 * With no cautions to come it is stopsFor.
 */
export function stopsWithCautions(o: CautionStopsInput): { likely: number; chance: Map<number, number>; underCaution: number } {
  const policy = o.rates.stopPolicy;
  const anyRate = o.rates.startPerH + o.rates.restartPerH + o.rates.middlePerH + o.rates.lastHourPerH > 0 && o.rates.meanMin > 0;
  if (!policy || !anyRate || !(o.lapS > 0)) {
    const n = stopsFor(o.cap, o.stintFuel, o.lapsLeft);
    return { likely: n, chance: new Map([[n, 1]]), underCaution: 0 };
  }
  const rnd = seeded(o.seed);
  const lastMax = lastStintMax(o.cap);
  const cautionLapS = o.lapS * o.outlook.lapRatio;
  const cautionLaps = () => Math.max(1, Math.round((-Math.log(1 - rnd()) * o.rates.meanMin * 60) / cautionLapS));
  const counts = new Map<number, number>();
  let byCaution = 0;
  for (let run = 0; run < CAUTION_STOP_RUNS; run++) {
    let f = o.stintFuel;
    let stops = 0;
    let at = o.outlook.nowS;
    let toGo = o.timeToGoS;
    let lastEnd = o.outlook.lastEndS;
    let yellow = 0;
    const decide = (startS: number, endBefore: number | null, toGoAtStart: number) => {
      // IMSA's short full-course yellow: the pit lane stays closed (isShortCaution).
      if (o.rates.shortCautionPitsClosed && isShortCaution(startS, endBefore, toGoAtStart)) return;
      decideStandard();
    };
    const decideStandard = () => {
      const canReach = o.classStint - f >= toGo / o.lapS - 1e-9;
      if (f > 0 && rnd() < cautionStopChance(policy, f / o.classStint, canReach, toGo <= LAST_PHASE_S)) {
        stops++;
        byCaution++;
        f = 0;
      }
    };
    if (o.underCaution) {
      yellow = cautionLaps();
      const startS = o.outlook.cautionStartS ?? o.outlook.nowS;
      // Race control's call, when the timing has it, before the timing rule.
      if (!o.stoppedThisCaution && !(o.rates.shortCautionPitsClosed && o.cautionShort === true)) {
        if (o.cautionShort === false) decideStandard();
        else decide(startS, lastEnd, o.timeToGoS + (o.outlook.nowS - startS));
      }
    }
    for (let k = 0; k < o.lapsLeft; k++) {
      const remaining = o.lapsLeft - k;
      if (yellow === 0) {
        const perH = phaseRatePerH(o.rates, at, lastEnd, toGo);
        if (rnd() < 1 - Math.exp(-(perH / 3600) * o.lapS)) {
          yellow = cautionLaps();
          decide(at, lastEnd, toGo);
        }
      }
      const use = yellow > 0 ? CAUTION_FUEL : 1;
      // Out of fuel before the flag: a stop (under green, or under the caution if it runs dry in it).
      if (f + use > o.cap + 1e-9 && f + remaining > lastMax + 1e-9) {
        stops++;
        f = 0;
      }
      f += use;
      if (yellow > 0) {
        at += cautionLapS;
        toGo -= cautionLapS;
        yellow--;
        if (yellow === 0) lastEnd = at;
      } else {
        at += o.lapS;
        toGo -= o.lapS;
      }
    }
    counts.set(stops, (counts.get(stops) ?? 0) + 1);
  }
  let likely = 0;
  let best = -1;
  for (const [n, c] of [...counts].sort((a, b) => a[0] - b[0])) if (c > best) [likely, best] = [n, c];
  return { likely, chance: new Map([...counts].sort((a, b) => a[0] - b[0]).map(([n, c]) => [n, c / CAUTION_STOP_RUNS])), underCaution: byCaution / CAUTION_STOP_RUNS };
}
/**
 * A splash's time lost: a fuel-only stop's (FuelStop) less the fuel time it does not take. This
 * share of a fuel-only stop does not depend on the fuel (the pit lane, stopping, getting away);
 * the rest goes with the fuel put in, a full tank taking all of it. From the Sunday 7 Hour 2026:
 * GTO's fuel-only stops about 66 s (each car's quickest, their median), #119's splash of about 4
 * laps' fuel 39.5 s: 53 %. Fitted again from the race's own splashes once it has one.
 */
export const SPLASH_FIXED = 0.55;
/**
 * A race shorter than this (s) is a sprint: a car's stops after its first are fuel-only (the first
 * changes drivers: Pilot Challenge 2026, 85% of first stops, 19-27% of later ones), charged at the
 * class's fuel-only stop. Pilot Challenge, Road Atlanta 2026, #67 at 1:20: its last stop was charged
 * 41 s in the net gap and 80 s on the pit-now card (the pooled class median, driver-change stops
 * included); it lost 62 s, and the fuel-only stop then read 57 s. Its last stop is the whole
 * fuel-only stop, not a splash sized to the fuel needed: the team fills the car. Charging the
 * pooled median instead over-ran by 16 s on the 2026 Pilot Challenge races (one stop to go: 59 s
 * charged as a splash, 60 s lost, 625 car-cuts; the pooled median read 78 s), so the fuel-only figure it is.
 */
export const SPRINT_S = 3 * 3600;
/** A splash puts in the laps of fuel needed and this many more. */
export const SPLASH_MARGIN_LAPS = 1;
/** At least this many such stints in the class before stops to go are estimated. */
export const MIN_STINTS = 3;

/**
 * What earlier races say about a class, for what this race has not shown yet (strategy-history.ts
 * works it out from finished races, only ever races before this one). Used to fill a gap, never
 * over the race's own figure: as a floor under it, the 2024-25 test raised the stops counted too
 * few in every bucket (LIVE_STRATEGY_AUDIT.md, the stop-priors branch).
 */
export interface StrategyHistory {
  /** Green laps' fuel the class's cars ran on one tank at this track (the latest earlier race here). */
  tankLaps?: number | null;
  /**
   * The tank in green running time (s) from the series' other tracks, for a first race at one: fuel
   * burns with time at racing load, so it carries between tracks within 3-8 % in WeatherTech where
   * laps carry within 25-40 % (AUDIT_REPORT.md 18.1). Laps here are this over the class's green lap.
   */
  tankGreenS?: number | null;
}
/**
 * A driver's record from the series' earlier races (RaceHistory.drivers, by driverKey): their green
 * lap over their car's pooled green lap in a race, the median over their races. 1.01: one per cent
 * slower than the car's pooled pace. Two drivers of one car differ by a median 0.7 % of the class
 * pace, 1.6 % at the upper quartile (AUDIT_REPORT.md 18.2).
 */
export interface DriverHistory {
  offset: number;
  /** Green laps behind the figure. */
  laps: number;
}
/** The history for a race: what earlier races say, by class name, and about each driver. */
export interface RaceHistory {
  classes: Record<string, StrategyHistory>;
  drivers?: Record<string, DriverHistory>;
}

/** The latest earlier race of the series at this track, in figures (GET /api/live/:slug/history `lastHere`), for "last time here". */
export interface LastHere {
  name: string | null;
  /** When it ran (ISO date). */
  date: string;
  race: {
    /** The overall leader's laps and race time (s). */
    laps: number;
    durationS: number | null;
    /** Caution periods and the leader's time under them (s). */
    cautions: number;
    cautionS: number;
    byClass: Record<string, { cars: number; finished: number; winnerStops: number | null; stopsMedian: number | null; fastestLapS: number | null }>;
  };
  /** Each class's green lap (s) in that race. */
  greenLapS: Record<string, number | null>;
}

/** A lap time or a span as the clock shows it: "1:28.4", "2h 01m", "14 min". */
export function lastHereClock(sec: number, kind: "lap" | "span"): string {
  if (kind === "lap") {
    const m = Math.floor(sec / 60);
    const s = sec - m * 60;
    return m > 0 ? `${m}:${s.toFixed(1).padStart(4, "0")}` : `${s.toFixed(1)} s`;
  }
  const h = Math.floor(sec / 3600);
  const min = Math.round((sec - h * 3600) / 60);
  return h > 0 ? `${h}h ${String(min).padStart(2, "0")}m` : `${min} min`;
}

/**
 * "Last time here" in one line for a class: "2025-10-01 · 72 laps in 2h 01m · 2 cautions, 15 min
 * yellow · GS: green lap 1:28.4, quickest 1:26.9, 2 stops for the winner (most cars 2), 18 of 20
 * finished." Null without a race or the class.
 */
export function lastHereText(h: LastHere | null | undefined, cls: string): string | null {
  if (!h) return null;
  const r = h.race;
  const c = r.byClass[cls];
  const parts = [
    `${r.laps} laps${r.durationS ? ` in ${lastHereClock(r.durationS, "span")}` : ""}`,
    r.cautions ? `${r.cautions} caution${r.cautions === 1 ? "" : "s"}, ${lastHereClock(r.cautionS, "span")} yellow` : "no cautions",
  ];
  if (c) {
    const g = h.greenLapS[cls];
    const bits = [
      g ? `green lap ${lastHereClock(g, "lap")}` : null,
      c.fastestLapS ? `quickest ${lastHereClock(c.fastestLapS, "lap")}` : null,
      c.winnerStops !== null ? `${c.winnerStops} stop${c.winnerStops === 1 ? "" : "s"} for the winner${c.stopsMedian !== null && c.stopsMedian !== c.winnerStops ? ` (most cars ${c.stopsMedian})` : ""}` : null,
      `${c.finished} of ${c.cars} finished`,
    ].filter(Boolean);
    parts.push(`${cls}: ${bits.join(", ")}`);
  }
  return parts.join(" · ");
}

/** A driver's name as the history keys it: lower case, accents off, one space between words. */
export function driverKey(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/**
 * The driver a stop marker names, as the entry lists them: the markers carry surnames (IMSA's time
 * cards), the entry full names. The entry's one driver with that surname; the name as given when
 * the entry does not settle it.
 */
export function entryDriver(name: string | null | undefined, drivers: string[] | undefined): string | null {
  if (!name) return null;
  const k = driverKey(name);
  if (!k) return null;
  const list = drivers ?? [];
  const exact = list.find((d) => driverKey(d) === k);
  if (exact) return exact;
  const words = k.split(" ").length;
  const ends = list.filter((d) => driverKey(d).split(" ").slice(-words).join(" ") === k);
  return ends.length === 1 ? ends[0] : name;
}

/**
 * Who drove each lap, from the stop markers' driver in and out: the first stint's driver is the
 * first stop's driver in; each stint after a stop is its driver out (else the next stop's driver
 * in). Null where no marker names anyone (a car yet to stop: one driver so far, whoever it is).
 */
export function driverByLap(car: Pick<CarData, "drivers">, pits: PitMarker[] | undefined): (lap: number) => string | null {
  const marks = [...(pits ?? [])].filter((m) => m.l > 0 && (m.inDriver || m.outDriver)).sort((a, b) => a.l - b.l);
  if (!marks.length) return () => null;
  const name = (n: string | undefined) => entryDriver(n, car.drivers);
  return (lap: number) => {
    let i = marks.length - 1;
    while (i >= 0 && marks[i].l >= lap) i--;
    // i: the last stop before this lap (-1: the first stint).
    if (i < 0) return name(marks[0].inDriver);
    return name(marks[i].outDriver) ?? (i + 1 < marks.length ? name(marks[i + 1].inDriver) : null);
  };
}
/**
 * A stop losing less than this is a pass through the pit lane (no fuel), not a stop: unless it
 * comes late in the car's stint (see markSplashes), where it is a splash of fuel.
 */
export const DRIVE_THROUGH_S = 45;

/**
 * A stop losing this much or more is a garage visit, as the race analysis counts one (its garage
 * threshold). A stop's own time can pass LONG_STOP_S: WRL Saturday 8 Hour at Watkins Glen 2026,
 * the fuel stops lost 300 to 450 s each, and at 300 s they were taken for garage visits.
 */
export const GARAGE_LOST_S = 600;
/** A splash of fuel spends at least this much longer in the pit lane than a pass through it (s). */
export const SPLASH_STOPPED_S = 8;
/**
 * How much of the projection to take (projectionShare): of the time each car's stops still to make
 * cost against the leader's (netGapS less gapS), and of its pace to the flag against the reference
 * pace (with its fuel saving's cost). PROJECTION_FAR_SHARE with PROJECTION_FAR_S or more to go,
 * all of it from PROJECTION_NEAR_S to go, in between in proportion. Far out, stop counts are often
 * a stop off and paces do not hold for hours; near the flag they do (Sunday 7 Hour 2026, lap 135:
 * the leader's extra stop decided it). On the 2026 development races, class place at the flag
 * within one: the full projection 64.7% (WRL) / 42.2% (WeatherTech), this 66.0% / 42.4%, today's
 * gaps 65.0% / 44.5%.
 */
export const PROJECTION_FAR_SHARE = 0.25;
export const PROJECTION_FAR_S = 7200;
export const PROJECTION_NEAR_S = 3600;
export function projectionShare(timeToGoS: number | null, nearShare = 1): number {
  if (timeToGoS === null || timeToGoS <= PROJECTION_NEAR_S) return nearShare;
  if (timeToGoS >= PROJECTION_FAR_S) return Math.min(PROJECTION_FAR_SHARE, nearShare);
  const far = Math.min(PROJECTION_FAR_SHARE, nearShare);
  return nearShare - ((nearShare - far) * (timeToGoS - PROJECTION_NEAR_S)) / (PROJECTION_FAR_S - PROJECTION_NEAR_S);
}
/** Without a stop under caution yet, one is taken to lose this share of a green stop. */
export const CAUTION_SHARE = 0.35;
/** A previous stint's pace (basePace): the median of up to this many of its green laps (all of a stint). */
const BASE_LAPS = 200;
/** Pace for the projection: the median of the car's last this many green laps (steadier than the last few). */
export const PROJECTION_LAPS = 10;
/** A caution stop is measured against the laps other cars finished within this many seconds of its own. */
const CAUTION_REF_S = 45;
/** A car this many paces past the latest crossing has stopped (or is in the garage). */
const STOPPED_PACES = 3;

export const CAUTION_FLAGS = new Set(["Yellow", "Code 35", "Code 60", "Red"]);

export interface Stop {
  lap: number;
  /** Time lost against green pace (s), when the timing measured it. */
  lostS: number | null;
  caution: boolean;
  kind: "stop" | "garage" | "drive_through";
  /** A drive-through only for its short time lost (the timing did not say so). */
  short?: boolean;
  /** A short visit late in the stint: a splash of fuel, so a stop (markSplashes). */
  splash?: boolean;
  /** Time in the pit lane (s), when the timing measured it. */
  laneS?: number | null;
  /**
   * The car is in this stop now: the marker is on its latest lap and the out-lap is not run, so
   * the time lost is not measured yet. Counted as a stop made (the stint ends), its time still to
   * be lost (owedStopsS). Before this, the provisional time lost (the in-lap's share, 12 s at Road
   * Atlanta 2026) made every stop in progress a drive-through: at 1:15 of the Pilot Challenge race
   * eleven GS stops were under way, 29 of 30 cars were shown owing a stop (10 did), and #67, the
   * one car still to stop, was shown P1 after stops.
   */
  inProgress?: boolean;
}

export interface CarStrategy {
  num: number;
  cls: string;
  team: string;
  posClass: number;
  laps: number;
  /** The driver in the car, as the stop markers name them (driverByLap); null where they name nobody. */
  driver: string | null;
  /** Green pace (s/lap) over its last laps; null without enough green laps. */
  pace: number | null;
  /**
   * Its green pace over its previous stint (the median of its green laps between its last two
   * stops): what a fuel-save target is set against, since the stint it is on may be run saving fuel
   * and its recent laps slower for it (lapping as before is no sign it is not saving: timing can
   * not show fuel). Null without a previous stint of MIN_PACE_LAPS green laps (its recent pace then).
   */
  basePace: number | null;
  stops: Stop[];
  /** What its next green stop loses (s): in a sprint, the class's fuel-only stop once it has made a stop; else the class's green stop. Null before one is measured. */
  nextStopS: number | null;
  /** The in-lap of its last stop (0 before the first). */
  lastStopLap: number;
  /** Laps since its last stop (the stint it is on). */
  stintLaps: number;
  /** Fuel it has used since its last stop, in green laps (a caution lap uses CAUTION_FUEL of one). */
  stintFuel: number;
  /** Laps of its stint run under a caution. */
  stintCautionLaps: number;
  /** Its longest stint between stops so far (green laps' fuel), not counting the one it is on. */
  longestStint: number;
  /** Green laps its fuel runs (the class's stint length, or its own longest when longer). Null before the class has run enough stints. */
  stintCap: number | null;
  /**
   * Laps it still has to run to the flag: the overall leader's laps to go (raceLapsToGo: the time to
   * go at the leader's green pace, less its stops still to make), plus one if it has yet to cross on the leader's lap. Null without a time to go.
   */
  lapsLeft: number | null;
  /** Laps it still has to run if no caution comes (LiveStrategyInput.greenLapsToGo): what its fuel is counted against. */
  fuelLapsLeft: number | null;
  /** Stops still needed to reach the flag on fuel, its laps to the flag taken as green (fuelLapsLeft); null when the stint length or pace is not known yet. */
  stopsToGo: number | null;
  /**
   * Stops it will most likely make with the cautions still to come (stopsWithCautions): a caution
   * often brings a stop before the fuel needs one. With how often each count came up. Null when
   * stopsToGo is, or when not asked for (LiveStrategyInput.cautionStops).
   */
  stopsLikely: number | null;
  stopsChance: Map<number, number> | null;
  /** Of those, how many on average it makes because a caution comes. */
  stopsUnderCaution: number | null;
  /** Seconds behind the class leader at the line now. */
  gapS: number;
  /** Behind the leader once both have made the stops they owe (green stops). */
  netGapS: number | null;
  netPos: number | null;
  /** Behind the leader at the flag, at today's paces. */
  projGapS: number | null;
  projPos: number | null;
  /** Not crossing the line any more (stopped, or a long stay in the garage). */
  stopped: boolean;
}

export interface PitWindow {
  /** The earliest lap it can stop and still need no more stops than now. */
  from: number;
  /** The last lap its stint cap reaches from its last stop (without saving fuel); never before the next lap. */
  to: number;
  /** Stops still needed, this one included. */
  stopsToGo: number;
}

export interface PitNow {
  /** A stop now under the current flag: time lost (s). */
  lostS: number;
  caution: boolean;
  /** The pit lane is closed to the car's class, so no stop now (LiveStrategy.pitLaneClosed). */
  pitClosed?: boolean;
  /** Why: a short yellow (closed until the restart), or a standard one not yet open to its class; and whether race control said so. */
  pitClosedShort?: boolean;
  pitClosedFromRc?: boolean;
  /** What it saves against a green stop (s); only under a caution. */
  savesS: number | null;
  /** Class position it rejoins in (the cars on track keep going). */
  rejoinPos: number;
  /** Its class position now. */
  nowPos: number;
  /** Measured from this race's stops, or taken as a share of a green stop. */
  measured: boolean;
  /**
   * Class position once the cars of the class due a stop (CYCLE_LAPS) have made theirs too, each
   * losing the same time; null before the class's stint length is known, or when no car is due.
   */
  settledPos: number | null;
  /** The cars of the class taken to stop within CYCLE_LAPS (their stint runs out by then). */
  dueNums: number[];
  /**
   * The same stop made after one and two more laps (pitNowLater): where it rejoins then, the
   * other cars moved on at their paces and those due by then having stopped; null where its
   * fuel does not run that far. Under green only.
   */
  later?: { laps: number; rejoinPos: number | null }[];
  /** What the laps say: "now" when waiting rejoins lower, "wait" (with the laps) when it rejoins higher, "same" otherwise. */
  trend?: { call: "now" | "wait" | "same"; laps: number; rejoinPos: number };
}

/** How many laps on the pit-now card's trend (PitNow.later). */
export const PIT_LATER_LAPS = 2;

/**
 * Where a car rejoins if it stops after `n` more laps (PitNow.later): each rival's gap to it moved
 * on by their paces' difference over those laps, the rivals due a stop within them losing the
 * same time, a car that has stopped and is ahead in class staying ahead. Null when the car's
 * fuel does not run `n` more laps, or its pace is unknown.
 */
export function pitNowLater(s: Pick<LiveStrategy, "cars">, c: CarStrategy, lost: number, n: number): number | null {
  if (c.pace === null || (c.stintCap !== null && c.stintCap - c.stintFuel < n)) return null;
  let ahead = 0;
  for (const o of s.cars) {
    if (o === c) continue;
    if (o.stopped) {
      if (o.posClass < c.posClass) ahead++;
      continue;
    }
    const drift = o.pace !== null ? n * (o.pace - c.pace) : 0;
    const w = pitWindowFor(o);
    const stops = w !== null && w.to <= o.laps + n ? lost : 0;
    if (o.gapS - c.gapS + drift + stops < lost) ahead++;
  }
  return ahead + 1;
}

/**
 * A car is due a stop when the last lap its stint can reach comes within this many laps. Back-tested
 * on the Sunday 7 Hour, COTA and Barber 2025-26: the position after the stop is within one place for
 * 74%, 54% and 82% of green stops, against 67%, 46% and 81% holding every other car on track.
 */
export const CYCLE_LAPS = 10;

export interface LiveStrategy {
  cls: string;
  cars: CarStrategy[];
  /** Time lost to a green stop / a stop under caution in this race (s); null before one is measured. */
  greenStopS: number | null;
  cautionStopS: number | null;
  cautionMeasured: boolean;
  /** How teams take stops under a caution in this series (the caution rates' policy): pitNowFor's chance each car stops too. */
  stopPolicy: CautionStopPolicy;
  /** The share of the projection taken (projectionShare); null without a projection. */
  projectionWeight?: number | null;
  /**
   * The pit lane is closed to this class under the caution under way: race control's calls when the
   * timing has them (pitLaneNow: closed until it opens for the class), else IMSA's timing rule for a
   * short yellow (isShortCaution: closed until the restart).
   */
  pitLaneClosed?: boolean;
  /** The caution is a short one (closed until the restart); with pitLaneFromRc, race control said so. */
  pitLaneShort?: boolean;
  /** pitLaneClosed and pitLaneShort come from race control's messages. */
  pitLaneFromRc?: boolean;
  /** Under a caution: how long it has run so far (s, from the leader's first lap in it), and the series' caution lengths. */
  cautionSoFarS?: number | null;
  cautionLengths?: CautionRates;
  /** The cautions card: the chance of another before the flag (cautionChance), and the race's cautions so far. */
  cautionOdds?: { chance: number; word: CautionWord } | null;
  cautionsSoFar?: { count: number; yellowS: number };
  /** What a stop takes for its fuel (splashLossS); null before the class has a green stop. */
  fuelStop: FuelStop | null;
  /** The class's stint length (green laps' fuel, CAUTION_FUEL); null before it has run MIN_STINTS long stints. */
  stintLength: number | null;
  /** Where the stint length comes from: this race's stints, or the history (StrategyHistory) while the race has not shown one. */
  stintLengthFrom: "race" | "history" | null;
  timeToGoS: number | null;
  caution: boolean;
  focus: { car: CarStrategy; window: PitWindow | null; pitNow: PitNow | null } | null;
}

export interface LiveStrategyInput {
  data: RaceChartData;
  annotations: AnnotationData | null;
  focusNum: number;
  flag: string | null;
  /** Race time still to run (s); null when the timing does not say. */
  timeToGoS: number | null;
  /** The race's laps to go when already worked out (raceLapsToGo); else it is worked out here. */
  lapsToGo?: number | null;
  /**
   * The race's laps to go if no caution comes (raceLapsToGo with NO_CAUTION_RATES): what the fuel
   * figures count against. Worked out here when not given; null to count fuel against lapsToGo.
   */
  greenLapsToGo?: number | null;
  /** How often cautions come and how long they last (WRL_CAUTION_RATES). */
  cautionRates?: CautionRates;
  /** Also work out each car's likely stops with the cautions to come (stopsLikely): the Strategy tab's figure; off by default (it runs a simulation per car). */
  cautionStops?: boolean;
  /** What earlier races say about each class (StrategyHistory), for what this race has not shown yet. */
  history?: RaceHistory | null;
}

type LapsToGoMemo = { data: RaceChartData; annotations: AnnotationData | null; ttg: number | null; rates: CautionRates; history: RaceHistory | null; v: number | null };
/** The last two answers (with and without the cautions to come, as liveStrategy asks for both). */
let lapsToGoMemo: LapsToGoMemo[] = [];

/**
 * The race's laps to go: as many as the car leading at the flag runs. The flag falls when the
 * leader then finishes, and that need not be the leader now: a leader with a stop more to make
 * can lose the lead to it (Sunday 7 Hour 2026, lap 135: #119 led owing a full stop and a splash,
 * #120 one stop and a fuel save; #120 led at the flag, 40 laps on, and counting #119's two stops
 * at a full stop's time each gave 39, which left #119 one stop to make and projected to win).
 *
 * Each car on the lead lap covers in the time to go, less its gap to the leader at the line, its
 * stops still to make (owedStopsS: the last for the fuel it needs) and any fuel saving's cost,
 * that time over its pace (its previous stint's when saving) in laps; the most any covers is the
 * laps to go. The stops depend on the laps, so it is worked out again until the two agree (the
 * longer when they swap between two). Without the leader's pace yet, its average lap. Worked out
 * once per chart and second (the header and every strategy ask).
 */
export function raceLapsToGo(data: RaceChartData, annotations: AnnotationData | null, timeToGoS: number | null, cautionRates: CautionRates = WRL_CAUTION_RATES, history: RaceHistory | null = null): number | null {
  const ttg = timeToGoS === null ? null : Math.round(timeToGoS);
  const m = lapsToGoMemo.find((x) => x.data === data && x.annotations === annotations && x.ttg === ttg && x.rates === cautionRates && x.history === history);
  if (m) return m.v;
  // Cautions to come, on average, make each lap to come longer than a green one.
  const slow = cautionOutlook(data, ttg, cautionRates).paceFactor;
  let v = lapsToGo(data, ttg);
  const k = overallLeaderKey(data);
  const fcy = fcySet(data);
  const green = k ? greenPace(data.cars[k].laps, data, fcy, PROJECTION_LAPS) : null;
  const paceS = green ? green * slow : null;
  if (k && paceS && ttg !== null && ttg > 0) {
    v = lapsToGo(data, ttg, { paceS });
    // The cars on the lead lap: their time behind the leader at the leader's lap count (one not
    // yet across it at its own pace).
    const clock = raceClock(data);
    const lead = clock.get(k)!;
    const L = data.cars[k].laps[data.cars[k].laps.length - 1].l;
    const tL = lead.get(L) ?? 0;
    const near: { key: string; g: number }[] = [];
    for (const [key, car] of Object.entries(data.cars)) {
      if (!car.laps.length) continue;
      const cl = car.laps[car.laps.length - 1].l;
      const own = clock.get(key)!;
      // Where it is now, at its green pace (the cautions to come do not move it now).
      const pace = greenPace(car.laps, data, fcy, PROJECTION_LAPS) ?? green!;
      const g = cl >= L ? (own.get(L) ?? 0) - tL : (own.get(cl) ?? 0) + (L - cl) * pace - tL;
      if (g >= 0 && g < pace) near.push({ key, g });
    }
    const seen = new Set<number>();
    for (let i = 0; i < 6 && v !== null; i++) {
      seen.add(v);
      // Each one's class strategy at these laps: its stops and fuel saving.
      const byClass = new Map<string, LiveStrategy | null>();
      let most = 0;
      for (const { key, g } of near) {
        const cls = data.cars[key].cls;
        if (!byClass.has(cls)) byClass.set(cls, liveStrategy({ data, annotations, focusNum: Number(key), flag: null, timeToGoS: ttg, lapsToGo: v, greenLapsToGo: null, cautionRates, history }));
        const st = byClass.get(cls);
        const c = st?.cars.find((x) => x.num === Number(key));
        if (!st || !c || c.stopped) continue;
        const r = reachToFlag(c);
        const pace = (r && r.save > 0 ? r.baseS : (c.pace ?? green!)) * slow;
        const lost = owedStopsS(c, c.nextStopS ?? st.greenStopS ?? 0, st.fuelStop, st.cautionStopS) + (r?.costS ?? 0);
        most = Math.max(most, (ttg - g - lost) / pace);
      }
      const next = Math.max(0, Math.ceil(most - 1e-9));
      if (next === v) break;
      if (seen.has(next)) {
        v = Math.max(v, next);
        break;
      }
      v = next;
    }
  }
  lapsToGoMemo = [{ data, annotations, ttg, rates: cautionRates, history, v }, ...lapsToGoMemo].slice(0, 2);
  return v;
}

/** The median of xs; null of none. */
export const median = (xs: number[]) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

function fcySet(data: RaceChartData): Set<number> {
  const s = new Set<number>();
  for (const [a, b] of data.fcy ?? []) for (let l = a; l <= b; l++) s.add(l);
  return s;
}

/** The car's stops: from the pit markers (as the Position Trace); a pit-lane start on lap 1 is not one. */
export function stopsOf(data: RaceChartData, annotations: AnnotationData | null, key: string, clock = raceClock(data)): Stop[] {
  const laps = data.cars[key]?.laps ?? [];
  const fcy = fcySet(data);
  const caution = (l: number) => {
    const lap = laps.find((x) => x.l === l);
    return fcy.has(l) || (!!lap?.flag && !isGreenFlag(lap.flag));
  };
  // One per visit: a marker on the lap after another (a visit flagged over two laps) is the same
  // stop, and so is a second marker on the same lap (IMSA charts: a bare one beside the timed one,
  // Sebring 2026 #4 lap 296), the timed one kept.
  const all = [...(annotations?.[key]?.pits ?? [])].sort((a, b) => a.l - b.l || Number(!!b.pitTiming) - Number(!!a.pitTiming));
  const marks = all.filter((p, i) => p.kind !== "retirement" && p.l > 1 && !(i > 0 && all[i - 1].l === p.l) && !(i > 0 && all[i - 1].l === p.l - 1 && !p.kind));
  const lastLap = laps.length ? laps[laps.length - 1].l : 0;
  if (marks.length) {
    return marks
      .map((m): Stop => {
        // On the car's latest lap with no out-lap run: the stop is under way and its loss unmeasured.
        const inProgress = m.l >= lastLap && !m.kind;
        const lost = !inProgress && typeof m.pitTiming?.totalPitLoss === "number" ? m.pitTiming.totalPitLoss : null;
        const ctx = m.pitTiming?.context;
        const under = ctx ? ctx !== "green" : caution(m.l);
        // Under a caution: against the cars on track, unless the race's analysis measured it so.
        const lostS = under && m.pitTiming?.lossMethod !== "fcy_neighbours" && m.pitTiming?.lossMethod !== "fcy_lap_field" ? cautionLoss(data, key, m.l, clock) : lost;
        // A garage visit by its time lost: under a caution (or a red flag) against the cars on track,
        // as every lap is slow then (Road America Sunday 2026, the stops at the red flag: 450 s laps).
        const garageBy = under ? lostS : lost;
        // A short time lost makes a pass through the pit lane under green; under a caution a full
        // stop loses little (Sunday 7 Hour 2026: #120's lap-33 stop, 37 s), so it stays a stop.
        const kind: Stop["kind"] =
          m.kind === "garage" || m.isGarage || (garageBy !== null && garageBy >= GARAGE_LOST_S)
            ? "garage"
            : m.kind === "drive_through" || m.pitTiming?.isDriveThrough || (lost !== null && lost < DRIVE_THROUGH_S && !under)
              ? "drive_through"
              : "stop";
        const short = kind === "drive_through" && m.kind !== "drive_through" && !m.pitTiming?.isDriveThrough;
        // The import's "total_only" timing repeats the time lost as the pit-lane time: not one.
        const t = m.pitTiming;
        const laneS = t?.pitRoadTime != null && t.pitRoadTime > 0 && !(t.decompositionLevel === "total_only" && t.pitRoadTime === t.totalPitLoss) ? t.pitRoadTime : null;
        return { lap: m.l, lostS: kind === "stop" ? lostS : lost, caution: under, kind, laneS, ...(short ? { short } : {}), ...(inProgress ? { inProgress } : {}) };
      })
      .sort((a, b) => a.lap - b.lap);
  }
  // No markers: the laps' pit flags, a visit over two laps counted once.
  const out: Stop[] = [];
  for (let i = 0; i < laps.length; i++) {
    const d = laps[i];
    if (d.pit !== 1 || d.l <= 1 || (i > 0 && laps[i - 1].pit === 1)) continue;
    const under = caution(d.l);
    out.push({ lap: d.l, lostS: under ? cautionLoss(data, key, d.l, clock) : null, caution: under, kind: d.ltSec >= LONG_STOP_S ? "garage" : "stop" });
  }
  return out;
}

/**
 * Short visits that were fuel stops. A visit losing under DRIVE_THROUGH_S is a pass through the
 * pit lane, unless the car came in at least STINT_SHARE of a class stint after its last stop: then
 * it is a splash of fuel near the end of its range (Sebring 2026: #119's 40 s stop 27 laps into a
 * stint, with 5 laps to go), which ends the stint like any stop. A drive-through penalty comes
 * whenever it is given, mostly early in a stint. The class stint here is the median of its long
 * stints between the plain stops (as liveStrategy's stint length), from MIN_STINTS of them.
 * Fuel takes time stopped: when the timing measured the visit's time in the pit lane and the
 * race's short visits early in a stint give the time to pass through it (passLaneEarly), a splash
 * spent SPLASH_STOPPED_S longer in it (Sebring: passes 10.6-13.6 s, #119's splash 41 s; #64's
 * 13.6 s visit late in its stint stays a pass).
 * Returns the stops with those visits made stops (splash: true), by car.
 */
export function markSplashes(stopsBy: Map<string, Stop[]>, keys: string[], passLaneS: number | null = null): Map<string, Stop[]> {
  const out = new Map(stopsBy);
  const reach = splashReach(stopsBy, keys);
  if (reach === null) return out;
  for (const k of keys) {
    let from = 0;
    out.set(
      k,
      (stopsBy.get(k) ?? []).map((s) => {
        let t = s;
        const stopped = passLaneS === null || s.laneS == null || s.laneS >= passLaneS + SPLASH_STOPPED_S;
        if (s.kind === "drive_through" && s.short && s.lap - from >= reach && stopped) t = { ...s, kind: "stop", splash: true };
        if (t.kind !== "drive_through") from = t.lap;
        return t;
      })
    );
  }
  return out;
}

/**
 * The class's reach for a splash: a short visit this far into a stint (STINT_SHARE of the median
 * of its long stints between plain stops) is late in it. Null before MIN_STINTS long stints.
 */
function splashReach(stopsBy: Map<string, Stop[]>, keys: string[]): number | null {
  const stints = keys.flatMap((k) => {
    const out: number[] = [];
    let from = 0;
    for (const s of stopsBy.get(k) ?? []) {
      if (s.kind === "drive_through") continue;
      if (s.lap > from) out.push(s.lap - from);
      from = s.lap;
    }
    return out;
  });
  const longest = Math.max(0, ...stints);
  const long = stints.filter((x) => x >= longest * STINT_SHARE);
  return long.length < MIN_STINTS ? null : median(long)! * STINT_SHARE;
}

/**
 * The time to pass through the pit lane, for telling a splash from a pass (markSplashes): the
 * median pit-lane time of the race's short visits early in a stint (before splashReach), where a
 * splash is not made. A short visit late in a stint is not counted: it may be the splash itself
 * (Sunday 7 Hour 2026: #119's 41 s splash was the race's only short visit, and judged against
 * itself it was a pass). Null without one.
 */
export function passLaneEarly(stopsBy: Map<string, Stop[]>, byClass: Map<string, string[]>): number | null {
  const lanes: number[] = [];
  for (const keys of byClass.values()) {
    const reach = splashReach(stopsBy, keys);
    if (reach === null) continue;
    for (const k of keys) {
      let from = 0;
      for (const s of stopsBy.get(k) ?? []) {
        if (s.kind === "drive_through") {
          if (s.short && s.laneS != null && s.lap - from < reach) lanes.push(s.laneS);
        } else from = s.lap;
      }
    }
  }
  return median(lanes);
}

/** The time to pass through the pit lane: the median pit-lane time of the race's short visits (null without one). */
export function passLane(stopsBy: Map<string, Stop[]>): number | null {
  return median([...stopsBy.values()].flatMap((st) => st.filter((s) => s.kind === "drive_through" && s.laneS != null).map((s) => s.laneS!)));
}

/** Each car's fuel splashes (markSplashes), by chart key: the laps of its short visits that were stops. */
export function splashLaps(data: RaceChartData, annotations: AnnotationData | null): Map<string, Set<number>> {
  const clock = raceClock(data);
  const all = new Map(Object.keys(data.cars).map((k) => [k, stopsOf(data, annotations, k, clock)]));
  const byClass = new Map<string, string[]>();
  for (const [k, c] of Object.entries(data.cars)) byClass.set(c.cls, [...(byClass.get(c.cls) ?? []), k]);
  const pass = passLaneEarly(all, byClass);
  const out = new Map<string, Set<number>>();
  for (const keys of byClass.values()) {
    const marked = markSplashes(all, keys, pass);
    for (const [k, st] of marked) {
      const laps = st.filter((s) => s.splash).map((s) => s.lap);
      if (laps.length) out.set(k, new Set(laps));
    }
  }
  return out;
}

/**
 * The driver term for greenPace: each lap scaled from its driver's record to the current driver's
 * (their offsets' ratio), so that right after a driver change the outgoing driver's laps are read
 * as the incoming one would run them. At a change the model's pace (the outgoing driver's last 10
 * laps) missed the stint to come by a median 1.0-1.2 % of the class pace; the car's pace with the
 * driver's offset by 0.15-0.17 % (AUDIT_REPORT.md 18.2). No term without the current driver's
 * record; a lap by a driver without one counts as the current driver's.
 */
export function driverAdjust(driver: string | null, drove: (lap: number) => string | null, drivers: Record<string, DriverHistory> | undefined): ((l: Lap) => number) | undefined {
  if (!driver || !drivers) return undefined;
  const now = drivers[driverKey(driver)]?.offset;
  if (!(now !== undefined && now > 0)) return undefined;
  return (l: Lap) => {
    const d = drove(l.l);
    const was = d ? drivers[driverKey(d)]?.offset : undefined;
    return was !== undefined && was > 0 ? now / was : 1;
  };
}

/**
 * Green pace: the average of the car's last PACE_LAPS green laps (no pit laps or out-laps, no
 * caution). `adjust` scales each lap (the driver term: another driver's laps to the one in the
 * car now, driverAdjust).
 */
export function greenPace(laps: Lap[], data: RaceChartData, fcy: Set<number>, n = PACE_LAPS, adjust?: (l: Lap) => number): number | null {
  const xs: number[] = [];
  for (let i = laps.length - 1; i >= 0 && xs.length < n; i--) {
    const l = laps[i];
    const prev = laps[i - 1];
    const out = !!prev && (prev.pit !== 0 || prev.ltSec >= data.greenPaceCutoff);
    if (l.pit === 0 && !out && l.ltSec > 1 && l.ltSec < data.greenPaceCutoff && !fcy.has(l.l) && (!l.flag || isGreenFlag(l.flag))) xs.push(adjust ? l.ltSec * adjust(l) : l.ltSec);
  }
  if (xs.length < MIN_PACE_LAPS) return null;
  return n > PACE_LAPS ? median(xs) : xs.reduce((a, b) => a + b, 0) / xs.length;
}

/**
 * Time lost to a stop under a caution: its in- and out-lap against the laps the cars on track
 * finished at about the same moments (the timing's own figure is against green pace, which a
 * caution lap is not).
 */
function cautionLoss(data: RaceChartData, key: string, lap: number, clock: Map<string, Map<number, number>>): number | null {
  const laps = data.cars[key]?.laps ?? [];
  const i = laps.findIndex((x) => x.l === lap);
  if (i < 0 || i + 1 >= laps.length) return null;
  const at = clock.get(key)!;
  const refAt = (t: number) => {
    const xs: number[] = [];
    for (const [k, c] of Object.entries(data.cars)) {
      if (k === key) continue;
      const ck = clock.get(k)!;
      for (let j = 1; j < c.laps.length; j++) {
        const l = c.laps[j];
        const e = ck.get(l.l)!;
        if (Math.abs(e - t) <= CAUTION_REF_S && l.pit === 0 && c.laps[j - 1].pit === 0 && l.ltSec > 1 && l.ltSec < LONG_STOP_S) xs.push(l.ltSec);
      }
    }
    return median(xs);
  };
  const a = refAt(at.get(laps[i].l)!);
  const b = refAt(at.get(laps[i + 1].l)!);
  return a === null || b === null ? null : laps[i].ltSec + laps[i + 1].ltSec - a - b;
}

/** Every car's race time at each of its laps (elapsedAt: the timing's own where it gives one). */
export function raceClock(data: RaceChartData): Map<string, Map<number, number>> {
  return new Map(Object.entries(data.cars).map(([k, c]) => [k, elapsedAt(c.laps)]));
}

/**
 * Each lap's end in race time: the timing's own (`et`) where it gives one, else the lap times added
 * up from the last lap that had one. A race time out of order with the laps after it is not taken. Lap times alone leave out a red flag's stoppage and some pit
 * time, so their sums drift apart from car to car (WRL 2026: minutes by the flag, a leader taken
 * for stopped and the laps to go put at 0).
 */
function elapsedAt(laps: Lap[]): Map<number, number> {
  // A race time later than a later lap's is wrong (WRL Road Atlanta 2026 Saturday, #43's lap 1 at
  // 1,610 s): only those no later lap undercuts are taken.
  const until: number[] = new Array(laps.length);
  let least = Infinity;
  for (let i = laps.length - 1; i >= 0; i--) {
    until[i] = least;
    const et = laps[i].et;
    if (typeof et === "number" && Number.isFinite(et)) least = Math.min(least, et);
  }
  const m = new Map<number, number>();
  let t = 0;
  laps.forEach((l, i) => {
    const et = l.et;
    const ok = typeof et === "number" && Number.isFinite(et) && et >= t && et <= until[i];
    t = ok ? et : t + (l.ltSec > 0 ? l.ltSec : 0);
    m.set(l.l, t);
  });
  return m;
}

const isCaution = (flag: string | null) => !!flag && CAUTION_FLAGS.has(flag);

/** The race's stops and what they cost: everything here depends on the chart alone (raceStops). */
export interface RaceStops {
  /** Each car's stops (stopsOf), all classes, with fuel splashes made stops (markSplashes). */
  stopsBy: Map<string, Stop[]>;
  /** Every car's summed lap times at each of its laps (raceClock). */
  clock: Map<string, Map<number, number>>;
  /** Time lost to each plain green / caution stop in the race, all classes (s). */
  greenLost: number[];
  cautionLost: number[];
}

const NO_ANNOTATIONS = {};
const raceStopsCache = new WeakMap<RaceChartData, WeakMap<object, RaceStops>>();

/**
 * The race's stops, worked out once per chart and pit markers. The live page holds new objects for
 * each chart version (live-wire.ts applyDelta), so a cached answer always belongs to the chart asked
 * about; callers must not change what it returns.
 */
export function raceStops(data: RaceChartData, annotations: AnnotationData | null): RaceStops {
  const annKey: object = annotations ?? NO_ANNOTATIONS;
  let byAnn = raceStopsCache.get(data);
  const hit = byAnn?.get(annKey);
  if (hit) return hit;
  const clock = raceClock(data);
  let stopsBy = new Map<string, Stop[]>();
  for (const k of Object.keys(data.cars)) stopsBy.set(k, stopsOf(data, annotations, k, clock));
  // A short visit late in a stint was a splash of fuel: a stop, ending the stint.
  const byClass = new Map<string, string[]>();
  for (const k of stopsBy.keys()) byClass.set(data.cars[k].cls, [...(byClass.get(data.cars[k].cls) ?? []), k]);
  const pass = passLaneEarly(stopsBy, byClass);
  for (const ks of byClass.values()) stopsBy = new Map([...stopsBy, ...markSplashes(stopsBy, ks, pass)]);
  // A splash is a short stop: not what a stop the car owes will cost.
  const greenLost: number[] = [];
  const cautionLost: number[] = [];
  for (const st of stopsBy.values()) for (const s of st) if (s.kind === "stop" && !s.splash && s.lostS !== null) (s.caution ? cautionLost : greenLost).push(s.lostS);
  const out: RaceStops = { stopsBy, clock, greenLost, cautionLost };
  if (!byAnn) raceStopsCache.set(data, (byAnn = new WeakMap()));
  byAnn.set(annKey, out);
  return out;
}

const stoppedCache = new WeakMap<RaceChartData, Set<number>>();

/**
 * The cars not crossing the line any more, by their laps: in each class, no lap for STOPPED_PACES of
 * its green pace (the class's cut-off without one) since the class's latest crossing. Worked out
 * once per chart (the live page holds a new chart object for each version). The live field
 * (live-field.ts stoppedCars) uses this for a car without a fresh GPS fix.
 */
export function stoppedByLaps(data: RaceChartData): Set<number> {
  const hit = stoppedCache.get(data);
  if (hit) return hit;
  const clock = raceClock(data);
  const fcy = fcySet(data);
  const byClass = new Map<string, string[]>();
  for (const [k, c] of Object.entries(data.cars)) if (c.laps.length) byClass.set(c.cls, [...(byClass.get(c.cls) ?? []), k]);
  const out = new Set<number>();
  for (const keys of byClass.values()) {
    const lastT = (k: string) => {
      const laps = data.cars[k].laps;
      return clock.get(k)!.get(laps[laps.length - 1].l) ?? 0;
    };
    const now = Math.max(...keys.map(lastT));
    for (const k of keys) {
      const pace = greenPace(data.cars[k].laps, data, fcy, PROJECTION_LAPS);
      if (now - lastT(k) > STOPPED_PACES * (pace ?? data.greenPaceCutoff)) out.add(Number(k));
    }
  }
  stoppedCache.set(data, out);
  return out;
}

/**
 * Time a green-flag stop loses in a class: the median of the class's plain green stops when it has
 * made at least 3, else the whole race's (null before any is measured).
 */
export function greenStopFor(race: RaceStops, keys: string[]): number | null {
  const clsGreen = keys.flatMap((k) => (race.stopsBy.get(k) ?? []).filter((s) => s.kind === "stop" && !s.splash && !s.caution && s.lostS !== null).map((s) => s.lostS!));
  return median(clsGreen.length >= 3 ? clsGreen : race.greenLost);
}

/** The focus car's class's green-flag stop (greenStopFor), for the map's pit ghost; null when the car is not in the chart. */
export function greenStopForCar(data: RaceChartData, annotations: AnnotationData | null, carNum: number): number | null {
  const car = data.cars[String(carNum)];
  if (!car) return null;
  const keys = Object.keys(data.cars).filter((k) => data.cars[k].cls === car.cls && data.cars[k].laps.length > 0);
  return keys.length ? greenStopFor(raceStops(data, annotations), keys) : null;
}

/**
 * A car's pit window: the earliest lap it can stop and still need no more stops than now (its last
 * stint saving what fuel it can, lastStintMax), to the last lap its stint cap reaches from its last stop
 * without saving any. Null when it owes no stop or a figure is not known yet.
 */
export function pitWindowFor(c: CarStrategy): PitWindow | null {
  const left = fuelLaps(c);
  if (c.stintCap === null || c.stopsToGo === null || c.stopsToGo <= 0 || left === null) return null;
  const finishLap = c.laps + left;
  // The last lap its fuel reaches, the laps from now taken as green.
  const to = Math.max(c.laps + 1, c.laps + Math.floor(c.stintCap - c.stintFuel + 1e-9));
  const from = Math.max(c.laps + 1, Math.ceil(finishLap - (c.stopsToGo - 1) * c.stintCap - lastStintMax(c.stintCap)));
  return { from: Math.min(from, to), to, stopsToGo: c.stopsToGo };
}

/**
 * Under a caution, the share of a stop's time lost within which a car behind gets past. Fitted on the
 * 2026 development races (14 WRL, 10 WeatherTech; leave one race out, flat from 0.15 to 0.3): a stop
 * under a caution costs far fewer places than its time lost against the cars on track suggests, as
 * the field runs slowly and closes up.
 */
export const CAUTION_PASS_SHARE = 0.25;

/** A car that stopped this many laps ago or fewer is taken to have made its stop in this caution. */
const JUST_STOPPED_LAPS = 2;

/**
 * Cars of the class that have stopped (retired, or a long stay in the garage) and stay ahead of a
 * running car: ahead in class now, and, at the flag (`atFlag`), with at least the laps it will have
 * run by then (its laps and laps to go), so it cannot pass them. The places those cars hold count
 * in its places after the stops and at the flag. On the 2026 WRL development races, the projected
 * place at the flag within one place over the last hour went from 86.9% to 90.8% counting them
 * (11% of the calls had such a car ahead), against 91.2% for the place now.
 */
export function stoppedAhead(cars: CarStrategy[], c: CarStrategy, atFlag: boolean): number {
  const runsTo = c.laps + (c.lapsLeft ?? 0);
  return cars.filter((o) => o !== c && o.stopped && o.posClass < c.posClass && (!atFlag || o.laps >= runsTo)).length;
}

/**
 * Where a car of the class rejoins if it stops now, under the current flag. Green: every car on
 * track less than the stop's time lost behind it at the line (and every car ahead) is ahead after.
 * This is the class order at the line; who is around it on the road at rejoin is the pit ghost's
 * (pit-ghost.ts). Under a caution: from its class place now, each car behind it within
 * CAUTION_PASS_SHARE of the stop's time lost gets past, unless it stops in this caution too (its
 * chance from the caution-stop policy, cautionStopChance); the expected count, rounded. The cars
 * that stop are allowed for there, so there is no separate place after the cycle.
 * Null for a car that has stopped, or before any stop's time lost is known.
 */
export function pitNowFor(
  s: Pick<LiveStrategy, "cars" | "caution" | "greenStopS" | "cautionStopS" | "cautionMeasured" | "timeToGoS"> & { stopPolicy?: CautionStopPolicy; pitLaneClosed?: boolean; pitLaneShort?: boolean; pitLaneFromRc?: boolean },
  c: CarStrategy
): PitNow | null {
  const lost = s.caution ? s.cautionStopS : s.greenStopS === null ? null : (c.nextStopS ?? s.greenStopS);
  if (lost === null || c.stopped) return null;
  if (s.caution && s.pitLaneClosed) {
    return { lostS: lost, caution: true, pitClosed: true, pitClosedShort: s.pitLaneShort ?? true, pitClosedFromRc: !!s.pitLaneFromRc, savesS: null, rejoinPos: c.posClass, nowPos: c.posClass, measured: s.cautionMeasured, settledPos: null, dueNums: [] };
  }
  if (s.caution) {
    const policy = s.stopPolicy ?? WRL_CAUTION_STOPS;
    const lastHour = s.timeToGoS !== null && s.timeToGoS <= LAST_PHASE_S;
    let places = 0;
    for (const o of s.cars) {
      if (o === c || o.stopped || o.posClass <= c.posClass) continue;
      if (Math.max(0, o.gapS - c.gapS) >= lost * CAUTION_PASS_SHARE) continue;
      places += 1 - cautionStopChanceOf(o, policy, lastHour);
    }
    return {
      lostS: lost,
      caution: true,
      savesS: s.greenStopS !== null ? Math.max(0, s.greenStopS - lost) : null,
      rejoinPos: c.posClass + Math.round(places),
      nowPos: c.posClass,
      measured: s.cautionMeasured,
      settledPos: null,
      dueNums: [],
    };
  }
  const after = c.gapS + lost;
  // A car that has stopped (retired, or a long stay in the garage) and is ahead in class stays
  // ahead: the stop now does not get past it.
  const ahead = s.cars.filter((o) => o !== c && (o.stopped ? o.posClass < c.posClass : o.gapS < after));
  // In a pit cycle the cars due a stop come in too: each loses the same time, so a car ahead now
  // that is due stays ahead only when it is still ahead after its own stop.
  const due = s.cars.filter((o) => {
    if (o === c || o.stopped) return false;
    const w = pitWindowFor(o);
    return w !== null && w.to <= o.laps + CYCLE_LAPS;
  });
  const dueSet = new Set(due);
  const settledAhead = s.cars.filter((o) => o !== c && (o.stopped ? o.posClass < c.posClass : o.gapS + (dueSet.has(o) ? lost : 0) < after));
  const rejoinPos = ahead.length + 1;
  const later = Array.from({ length: PIT_LATER_LAPS }, (_, i) => ({ laps: i + 1, rejoinPos: pitNowLater(s, c, lost, i + 1) }));
  const known = later.filter((x): x is { laps: number; rejoinPos: number } => x.rejoinPos !== null);
  const best = known.length ? known.reduce((a, b) => (b.rejoinPos < a.rejoinPos ? b : a)) : null;
  const trend: PitNow["trend"] =
    best === null
      ? undefined
      : best.rejoinPos < rejoinPos
        ? { call: "wait", laps: best.laps, rejoinPos: best.rejoinPos }
        : known.some((x) => x.rejoinPos > rejoinPos)
          ? { call: "now", laps: known.find((x) => x.rejoinPos > rejoinPos)!.laps, rejoinPos: known.find((x) => x.rejoinPos > rejoinPos)!.rejoinPos }
          : { call: "same", laps: known[known.length - 1].laps, rejoinPos };
  return {
    lostS: lost,
    caution: s.caution,
    savesS: s.caution && s.greenStopS !== null ? Math.max(0, s.greenStopS - lost) : null,
    rejoinPos,
    nowPos: c.posClass,
    measured: s.caution ? s.cautionMeasured : s.greenStopS !== null,
    settledPos: due.length ? settledAhead.length + 1 : null,
    dueNums: due.map((o) => o.num),
    later,
    ...(trend ? { trend } : {}),
  };
}

/** The chance a car stops in this caution (pitNowFor): none when it has just stopped; even without a stint length. */
function cautionStopChanceOf(o: CarStrategy, policy: CautionStopPolicy, lastHour: boolean): number {
  if (o.lastStopLap > 0 && o.laps - o.lastStopLap <= JUST_STOPPED_LAPS) return 0.05;
  if (o.stintCap === null || !(o.stintCap > 0)) return 0.5;
  const reach = o.lapsLeft !== null && o.stintCap - o.stintFuel >= o.lapsLeft;
  return cautionStopChance(policy, o.stintFuel / o.stintCap, reach, lastHour);
}

/** With this many laps to spare or fewer, reaching the flag is tight. */
export const TIGHT_LAPS = 2;

export interface ReachToFlag {
  /** Laps to the flag if no caution comes (fuelLapsLeft: the leader's, as it runs them). */
  toFlag: number;
  /** The whole stint at the flag, in green laps' fuel: the fuel used so far (none saved) and the laps to the flag, taken as green. */
  stint: number;
  /** Its stint cap: the green laps its fuel runs without saving any. */
  cap: number;
  /** Laps to spare within the cap (0 when it needs to save fuel). */
  spare: number;
  /** Laps of fuel to save over the stint (0 to SAVE_LAPS). */
  save: number;
  /**
   * The share of each lap's fuel that saves them, over the whole stint from its last stop (0 to
   * MAX_SAVE_SHARE). Timing can not show fuel saved (a car saving it laps much as before: #120,
   * Sunday 7 Hour 2026), so the stint is judged whole: a car saving from its stop stays on its
   * plan, where judging from now would call for more each lap until it asked for a stop.
   */
  saveShare: number;
  /** A lap or two to spare, or fuel to save. */
  tight: boolean;
  /** When it has fuel to save: the lap time that saves it, run from its stop to the flag (fuelSaveLap). */
  targetS: number | null;
  /** The time that target loses over the laps to the flag (s). */
  costS: number | null;
  /** The pace the target is set against (s): its previous stint's (basePace), else its recent pace. */
  baseS: number;
  /** `baseS` is its previous stint's pace. */
  baseFromStint: boolean;
  /**
   * If it has saved none yet: the share of each lap's fuel to save from now, the lap time that
   * does it, and whether that can be saved (canStretch; if not, a splash). Null when there is no
   * fuel to save or no lap of the stint run yet.
   */
  fromNow: { share: number; targetS: number | null; ok: boolean } | null;
}

/**
 * The lap time that gets a car through `laps` laps on `fuelLaps` laps of fuel (at its pace): each
 * lap saving 1 − fuelLaps / laps of its fuel, at FUEL_PER_TIME % of fuel for each % of lap time,
 * to the tenth above. The laps stay the same: the flag falls when the leader finishes, so a lap
 * given up to save fuel is a lap lost to the leader, not one fewer to run. Null when the fuel can
 * not be saved (canStretch).
 */
export function fuelSaveLap(paceS: number, fuelLaps: number, laps: number): number | null {
  if (!(paceS > 0) || !(laps > 0) || !canStretch(fuelLaps, laps)) return null;
  const share = Math.max(0, 1 - fuelLaps / laps);
  return Math.ceil(paceS * (1 + share / FUEL_PER_TIME) * 10 - 1e-9) / 10;
}

/**
 * For a car that can reach the flag (no stops to go): the laps to the flag (the leader's, as it
 * runs them), the whole stint that makes against its stint cap, and the laps to spare, or the
 * laps of fuel it would have to save over the stint (canStretch, as stops to go reckons it), the
 * lap time that saves them, and what it takes from now if it has saved none yet. Null when it
 * owes a stop or any figure is not known.
 */
export function reachToFlag(c: CarStrategy): ReachToFlag | null {
  const toFlag = fuelLaps(c);
  if (c.stopsToGo !== 0 || c.stintCap === null || c.pace === null || !(c.pace > 0) || toFlag === null) return null;
  // The pace the target is set against: its previous stint's, not its recent laps (which saving slows).
  const fromStint = c.basePace != null && c.basePace > 0;
  const base = fromStint ? c.basePace! : c.pace;
  const cap = c.stintCap;
  // The whole stint in green laps' fuel (caution laps so far used CAUTION_FUEL of one).
  const stint = Math.round((c.stintFuel + toFlag) * 10) / 10;
  const spare = Math.max(0, Math.round((cap - stint) * 10) / 10);
  const save = Math.min(SAVE_LAPS, Math.max(0, Math.round((stint - cap) * 10) / 10));
  const saveShare = save > 0 ? Math.min(MAX_SAVE_SHARE, 1 - cap / stint) : 0;
  const targetS = save > 0 ? fuelSaveLap(base, cap, stint) : null;
  const costS = targetS !== null ? toFlag * (targetS - base) : null;
  // Saved none yet: the fuel left (at its pace) over the laps to the flag.
  const left = cap - c.stintFuel;
  const fromNow =
    save > 0 && c.stintFuel > 0 && toFlag > 0
      ? { share: Math.max(0, 1 - left / toFlag), targetS: fuelSaveLap(base, left, toFlag), ok: canStretch(left, toFlag) }
      : null;
  return { toFlag, stint, cap, spare, save, saveShare, tight: save > 0 || spare <= TIGHT_LAPS, targetS, costS, fromNow, baseS: base, baseFromStint: fromStint };
}

export interface FuelStop {
  /** A fuel-only stop's time lost (s): each car's quickest green stop in the class, their median. */
  fullS: number;
  /** The share of it that does not depend on the fuel (SPLASH_FIXED, or fitted from the race's splashes). */
  fixed: number;
  /** `fixed` fitted from this race's splashes. */
  fitted: boolean;
}

/**
 * Time lost to a stop putting in `fuelLaps` laps of fuel (green laps) into a tank of `cap`: the
 * fuel-only stop's fixed part, and its fuel part in proportion. A splash of a few laps' fuel loses
 * a little over the fixed part; a full tank, the whole fuel-only stop.
 */
export function splashLossS(f: FuelStop, fuelLaps: number, cap: number): number {
  const share = cap > 0 ? Math.min(1, Math.max(0, fuelLaps / cap)) : 1;
  return f.fullS * (f.fixed + (1 - f.fixed) * share);
}

/**
 * The stops a car owes, in time lost: each a typical green stop (`greenS`), but the last putting in
 * only the fuel it needs to the flag (the laps past the fuel it has and the full tanks of the stops
 * before it, SPLASH_MARGIN_LAPS more), and losing no more than a typical stop: a top-up near the
 * end is a splash, not a full stop.
 */
export function owedStopsS(c: CarStrategy, greenS: number, f: FuelStop | null, cautionS: number | null = null): number {
  // A stop under way (Stop.inProgress) has its time still to lose: the class's green stop,
  // or its caution stop when the stop is under a caution (and one is measured).
  const under = c.stops?.length ? c.stops[c.stops.length - 1] : null;
  const pending = under?.inProgress ? (under.caution && cautionS !== null ? cautionS : greenS) : 0;
  const k = c.stopsToGo ?? 0;
  if (k <= 0) return pending;
  if (!f || c.stintCap === null || c.lapsLeft === null) return pending + k * greenS;
  const need = c.stintFuel + c.lapsLeft - c.stintCap - (k - 1) * c.stintCap + SPLASH_MARGIN_LAPS;
  const last = Math.min(greenS, splashLossS(f, Math.max(1, need), c.stintCap));
  return pending + (k - 1) * greenS + last;
}

/** Where an option leaves a car at the flag: its class place and the cars either side, with the gaps (s). */
export interface AtFlag {
  pos: number;
  ahead: { num: number; gapS: number } | null;
  behind: { num: number; gapS: number } | null;
}

export interface FuelSaveOutcome {
  /** Class position at the flag saving the fuel (the projection, with the time the target loses). */
  savePos: number;
  /** With a splash instead (racing to the flag at its pace, the splash's time lost). */
  splashPos: number;
  /** Each option at the flag, with the cars either side. */
  save: AtFlag;
  splash: AtFlag;
  /** A splash's time lost (s): splashLossS for the laps of fuel it would put in (`splashLaps`). */
  splashS: number;
  splashLaps: number;
  /** The splash's fuel-free part fitted from this race's splashes (else SPLASH_FIXED). */
  splashFitted: boolean;
  /**
   * The car that would finish just behind it saving fuel at the target, and the slowest lap that
   * keeps it there (its pace plus the projected margin over the laps to the flag); null with no car behind.
   */
  hold: { num: number; lapS: number; owesStop: boolean } | null;
  /** The car that finishes just ahead of it when saving costs it a place, and whether it owes a stop. */
  lost: { num: number; owesStop: boolean } | null;
}

/**
 * Saving fuel against a splash, for a car that has fuel to save: where each leaves it at the flag
 * in the class projection (gaps after the stops every car owes, each car's pace to the flag, and
 * each car's own fuel saving). The splash puts in the fuel it would otherwise save and a lap more
 * (splashLossS). The projection's car behind is the one that can pass it: a car
 * behind on the road that owes a stop it does not, or a car ahead on the road that owes one, are
 * placed after that stop, so the room it has to slow down counts their stops. Null without a
 * projection, a stop's time, or fuel to save.
 */
export function fuelSaveOutcome(s: Pick<LiveStrategy, "cars" | "greenStopS" | "fuelStop"> & { timeToGoS?: number | null; projectionWeight?: number | null }, c: CarStrategy): FuelSaveOutcome | null {
  const r = reachToFlag(c);
  // A splash instead: the fuel it would otherwise save, and a lap more.
  const splashLaps = r ? Math.ceil(r.save) + SPLASH_MARGIN_LAPS : 0;
  const splashS = r && s.fuelStop ? splashLossS(s.fuelStop, splashLaps, r.cap) : s.greenStopS;
  if (!r || r.save === 0 || r.costS === null || r.targetS === null || c.projGapS === null || splashS === null || c.pace === null) return null;
  // Its projected gap without its own fuel saving (the projection adds each car's, in part).
  const base = c.projGapS - (s.projectionWeight ?? projectionShare(s.timeToGoS ?? null)) * r.costS;
  const others = s.cars.filter((o) => o !== c && !o.stopped && o.projGapS !== null);
  const posWith = (lost: number) => 1 + others.filter((o) => o.projGapS! < base + lost).length;
  const owes = (o: CarStrategy) => (o.stopsToGo ?? 0) > 0;
  // Each option at the flag: its place and the cars either side.
  const atFlag = (lost: number): AtFlag => {
    const me = base + lost;
    const a = others.filter((o) => o.projGapS! < me).sort((x, y) => y.projGapS! - x.projGapS!)[0];
    const b = others.filter((o) => o.projGapS! >= me).sort((x, y) => x.projGapS! - y.projGapS!)[0];
    return { pos: posWith(lost), ahead: a ? { num: a.num, gapS: me - a.projGapS! } : null, behind: b ? { num: b.num, gapS: b.projGapS! - me } : null };
  };
  // Just behind it saving fuel, and just ahead.
  const behind = others.filter((o) => o.projGapS! >= base + r.costS!).sort((a, b) => a.projGapS! - b.projGapS!)[0];
  const ahead = others.filter((o) => o.projGapS! < base + r.costS! && o.projGapS! >= base).sort((a, b) => b.projGapS! - a.projGapS!)[0];
  return {
    savePos: posWith(r.costS),
    splashPos: posWith(splashS),
    save: atFlag(r.costS),
    splash: atFlag(splashS),
    splashS,
    splashLaps,
    splashFitted: !!s.fuelStop?.fitted,
    hold: behind ? { num: behind.num, lapS: Math.floor((r.baseS + (behind.projGapS! - base) / r.toFlag) * 10) / 10, owesStop: owes(behind) } : null,
    lost: ahead ? { num: ahead.num, owesStop: owes(ahead) } : null,
  };
}

/**
 * The pit-now trend in words (PitNow.trend): "wait: P14 in 2 laps" when waiting rejoins higher,
 * "box now: P18 in 2 laps" when it rejoins lower, "same in 2 laps" otherwise; null without one.
 */
export function pitTrendText(p: PitNow): string | null {
  if (!p.trend) return null;
  const laps = `${p.trend.laps} lap${p.trend.laps === 1 ? "" : "s"}`;
  if (p.trend.call === "wait") return `wait: P${p.trend.rejoinPos} in ${laps}`;
  if (p.trend.call === "now") return `box now: P${p.trend.rejoinPos} in ${laps}`;
  return `same in ${laps}`;
}

/** The pit-now card's arrow for the trend: up when waiting rejoins higher, down when it rejoins lower. */
export function pitTrendArrow(p: PitNow): string {
  return p.trend?.call === "wait" ? "▲" : p.trend?.call === "now" ? "▼" : "";
}

/** "P4 once #12 and #34, due a stop within 10 laps, have made theirs." (null without a settled position). */
export function settledText(p: PitNow, name: (num: number) => string): string | null {
  if (p.settledPos === null || !p.dueNums.length) return null;
  const cars = p.dueNums.map(name);
  const list = cars.length <= 2 ? cars.join(" and ") : `${cars.slice(0, -1).join(", ")} and ${cars[cars.length - 1]}`;
  return `P${p.settledPos} once ${list}, due a stop within ${CYCLE_LAPS} laps, ${cars.length === 1 ? "has made its" : "have made theirs"}.`;
}

/**
 * "If it stops now" for every car with laps, each against its own class (pitNowFor), by chart key:
 * the Live Crossing Order's Box column. The race's stops are shared (raceStops), so each class
 * costs only its own cars.
 */
export function pitNowAll(data: RaceChartData, annotations: AnnotationData | null, flag: string | null, timeToGoS: number | null, cautionRates: CautionRates = WRL_CAUTION_RATES, history: RaceHistory | null = null): Map<number, PitNow> {
  const out = new Map<number, PitNow>();
  const first = new Map<string, string>();
  for (const [k, c] of Object.entries(data.cars)) if (c.laps.length && !first.has(c.cls)) first.set(c.cls, k);
  for (const k of first.values()) {
    const s = liveStrategy({ data, annotations, focusNum: Number(k), flag, timeToGoS, cautionRates, history });
    if (!s) continue;
    for (const c of s.cars) {
      const p = pitNowFor(s, c);
      if (p) out.set(c.num, p);
    }
  }
  return out;
}

/**
 * The class's stint length from its history (StrategyHistory) while the race has not shown one:
 * the tank in laps at this track, else the tank in green seconds over the class's green lap now
 * (the median of its cars' paces; nothing before the class has a pace). Never shorter than a stint
 * a running car is on now.
 */
export function historyTank(h: StrategyHistory | null | undefined, paces: (number | null)[], onNow = 0): number | null {
  if (!h) return null;
  let laps: number | null = null;
  if (h.tankLaps != null && h.tankLaps > 0) laps = h.tankLaps;
  else if (h.tankGreenS != null && h.tankGreenS > 0) {
    const lap = median(paces.filter((p): p is number => p !== null && p > 0));
    if (lap != null) laps = h.tankGreenS / lap;
  }
  return laps === null ? null : Math.round(Math.max(laps, onNow));
}

export function liveStrategy({ data, annotations, focusNum, flag, timeToGoS, lapsToGo: givenLapsToGo, greenLapsToGo: givenGreenLapsToGo, cautionRates = WRL_CAUTION_RATES, cautionStops = false, history = null }: LiveStrategyInput): LiveStrategy | null {
  const focus = data.cars[String(focusNum)];
  if (!focus) return null;
  const cls = focus.cls;
  const keys = Object.keys(data.cars).filter((k) => data.cars[k].cls === cls && data.cars[k].laps.length > 0);
  if (!keys.length) return null;
  const fcy = fcySet(data);
  const caution = isCaution(flag);

  // The race's stops, all classes (once per chart): a green stop's and a caution stop's time lost.
  const race = raceStops(data, annotations);
  const { stopsBy, clock } = race;
  const greenStopS = greenStopFor(race, keys);
  // A fuel-only stop: each car's quickest green stop (no splash), the median over the class.
  const quickest = keys
    .map((k) => Math.min(...(race.stopsBy.get(k) ?? []).filter((x) => x.kind === "stop" && !x.splash && !x.caution && x.lostS !== null && x.lostS > 0).map((x) => x.lostS!)))
    .filter((x) => Number.isFinite(x));
  const fuelOnlyS = median(quickest);
  // A sprint (SPRINT_S): the race's length is the time to go plus the overall leader's race time.
  const leaderKey = overallLeaderKey(data);
  const leaderNow = leaderKey ? Math.max(0, ...(clock.get(leaderKey)?.values() ?? [])) : 0;
  const sprint = timeToGoS !== null && timeToGoS + leaderNow < SPRINT_S;
  const nextStopSFor = (stops: Stop[]): number | null => {
    if (!sprint) return greenStopS;
    const made = stops.some((x) => x.kind === "stop" && !x.splash);
    return (made ? fuelOnlyS : null) ?? greenStopS;
  };
  const measuredCaution = median(race.cautionLost);
  const cautionStopS = measuredCaution ?? (greenStopS !== null ? greenStopS * CAUTION_SHARE : null);

  // "Now": the latest crossing in the class (summed lap times).
  const elapsed = clock;
  const lastT = (k: string) => {
    const laps = data.cars[k].laps;
    return elapsed.get(k)!.get(laps[laps.length - 1].l) ?? 0;
  };
  // Not crossing the line any more (the same rule for every class: stoppedByLaps).
  const lapStopped = stoppedByLaps(data);

  // Stints between stops (fuel stops and garage visits end one; a drive-through does not).
  const rows = keys.map((k) => {
    const car = data.cars[k];
    const laps = car.laps;
    const lapsDone = laps[laps.length - 1].l;
    const endStops = (stopsBy.get(k) ?? []).filter((s) => s.kind !== "drive_through");
    const ends = endStops.map((s) => s.lap);
    // Stints that ended in the garage are not fuel stints (the class's stint length leaves them out).
    const garageEnds = new Set(endStops.filter((s) => s.kind === "garage").map((s) => s.lap));
    // Fuel used by each lap's end, in green laps: a caution lap uses CAUTION_FUEL of one.
    const fuelTo = new Map<number, number>([[0, 0]]);
    const cautionTo = new Map<number, number>([[0, 0]]);
    let f = 0;
    let nc = 0;
    for (const l of laps) {
      const yellow = fcy.has(l.l) || (!!l.flag && !isGreenFlag(l.flag));
      f += yellow ? CAUTION_FUEL : 1;
      if (yellow) nc++;
      fuelTo.set(l.l, f);
      cautionTo.set(l.l, nc);
    }
    const fuelAt = (l: number) => fuelTo.get(l) ?? l;
    const stints: number[] = [];
    const fuelStints: number[] = [];
    // Its splashes: the fuel used before each, and the fuel run on it to its next stop (null: the stint it is on).
    const splashes: { lostS: number; fuelBefore: number; fuelAfter: number | null }[] = [];
    const splashLapsSet = new Set((stopsBy.get(k) ?? []).filter((x) => x.splash && !x.caution && x.lostS !== null).map((x) => x.lap));
    let from = 0;
    for (let i = 0; i < ends.length; i++) {
      const e = ends[i];
      if (e > from) {
        stints.push(fuelAt(e) - fuelAt(from));
        if (!garageEnds.has(e)) fuelStints.push(fuelAt(e) - fuelAt(from));
      }
      if (splashLapsSet.has(e)) {
        const st = (stopsBy.get(k) ?? []).find((x) => x.lap === e)!;
        splashes.push({ lostS: st.lostS!, fuelBefore: fuelAt(e) - fuelAt(from), fuelAfter: i + 1 < ends.length ? fuelAt(ends[i + 1]) - fuelAt(e) : null });
      }
      from = e;
    }
    const stintFuel = fuelAt(lapsDone) - fuelAt(from);
    const stintCautionLaps = (cautionTo.get(lapsDone) ?? 0) - (cautionTo.get(from) ?? 0);
    // The driver term: with the driver now in the car on record, another driver's laps count as
    // theirs would (driverAdjust); a pace window all their own is unchanged.
    const drove = driverByLap(car, annotations?.[k]?.pits);
    const driver = drove(lapsDone);
    const adjust = driverAdjust(driver, drove, history?.drivers);
    const pace = greenPace(laps, data, fcy, PROJECTION_LAPS, adjust);
    // Its previous stint's green pace: the median of all its green laps between its last two stops.
    const prevFrom = ends.length >= 2 ? ends[ends.length - 2] : 0;
    const basePace = ends.length ? greenPace(laps.filter((l) => l.l > prevFrom && l.l <= from), data, fcy, BASE_LAPS, adjust) : null;
    const stopped = lapStopped.has(Number(k));
    return { k, car, lapsDone, stints, fuelStints, lastStopLap: from, stintFuel, stintCautionLaps, splashes, pace, basePace, stopped, driver };
  });

  // The class's stint length: the long stints (within STINT_SHARE of the longest) it has run, and
  // never shorter than a stint a running car is on now (early stops are often short ones). The
  // longest must be corroborated: another stint within STINT_SHARE of it. One stint read over a
  // stop the chart missed, or into the garage, stood alone above every real stint in a quarter to
  // a third of the class-cuts of the 2026 and 2024-25 races, and the class then had no stint
  // length at all. Stints that ended in the garage are left out.
  const all = rows.flatMap((r) => r.fuelStints);
  const onNow = Math.max(0, ...rows.filter((r) => !r.stopped).map((r) => r.stintFuel));
  const ranked = [...all].sort((a, b) => b - a);
  const longest = ranked.find((s, i) => i === ranked.length - 1 || ranked[i + 1] >= s * STINT_SHARE) ?? 0;
  const long = all.filter((s) => s >= longest * STINT_SHARE);
  // In a sprint the first stints end on the driver change, not the tank (Road Atlanta 2026 GS:
  // stops on laps 27-31 of a 32-lap tank; six cars five laps into their last stint were then shown
  // owing a splash on a 29-lap cap, and none stopped again): the class's tank there is its longest
  // corroborated stint, not the median of its long ones.
  const own = long.length >= MIN_STINTS ? Math.round(Math.max(sprint ? longest : median(long)!, onNow)) : null;
  const stintLength = own ?? historyTank(history?.classes[cls], rows.map((r) => r.pace), onNow);
  const stintLengthFrom: LiveStrategy["stintLengthFrom"] = own !== null ? "race" : stintLength !== null ? "history" : null;

  // The class leader: the most laps, then the earliest to complete them.
  const order = [...rows].sort((a, b) => b.lapsDone - a.lapsDone || lastT(a.k) - lastT(b.k));
  const lead = order[0];
  const leadAt = elapsed.get(lead.k)!;
  const gapOf = (r: (typeof rows)[number]) => {
    const t = lastT(r.k);
    const lt = leadAt.get(r.lapsDone);
    // Behind at the line: its time at its last lap against the leader's at the same lap.
    return lt === undefined ? 0 : Math.max(0, t - lt);
  };
  const refPace = lead.pace ?? median(rows.map((r) => r.pace).filter((p): p is number => p !== null));

  // Laps each car still runs to the flag, from the overall leader's laps to go and the two paces
  // (a car in a slower class runs fewer than the leader).
  const leadKey = overallLeaderKey(data);
  const outlook = cautionOutlook(data, timeToGoS, cautionRates);
  // Race control's pit-lane calls for this class under the caution under way (IMSA), when the timing has them.
  const rcPits = pitLaneNow(data, cls, caution);
  const leadGreen = leadKey ? greenPace(data.cars[leadKey].laps, data, fcy, PROJECTION_LAPS) : null;
  const raceToGo = givenLapsToGo !== undefined ? givenLapsToGo : raceLapsToGo(data, annotations, timeToGoS, cautionRates, history);
  const lapsLeftBy = lapsLeftByCar(data, raceToGo, {
    leadS: leadGreen !== null ? leadGreen * outlook.paceFactor : null,
    byCar: new Map(rows.map((r) => [Number(r.k), r.pace !== null ? r.pace * outlook.paceFactor : null])),
    timeToGoS,
  });
  // The fuel figures count the laps to go if no caution comes: the most laps the car can have to
  // run, so the fuel it needs (a caution only saves fuel). On the 2026 development races, stops to
  // go on fuel this way matched what cars did more often than with the cautions to come allowed
  // for (WRL 38% vs 37% exact, WeatherTech 39% vs 37%), and "can reach the flag" calls held more
  // often (WRL 61% vs 47% with a lap or two to spare).
  const noCautions = cautionRates.startPerH + cautionRates.restartPerH + cautionRates.middlePerH + cautionRates.lastHourPerH <= 0 || !(cautionRates.meanMin > 0);
  const greenToGo = givenGreenLapsToGo !== undefined ? givenGreenLapsToGo : noCautions ? raceToGo : raceLapsToGo(data, annotations, timeToGoS, NO_CAUTION_RATES, history);
  const fuelLapsBy =
    greenToGo === null
      ? null
      : lapsLeftByCar(data, greenToGo, {
          leadS: leadGreen,
          byCar: new Map(rows.map((r) => [Number(r.k), r.pace])),
          timeToGoS,
        });
  const cars: CarStrategy[] = order.map((r, i) => {
    const longestStint = Math.max(0, ...r.stints);
    const stintCap = stintLength === null ? null : Math.max(stintLength, longestStint);
    const stintLaps = r.lapsDone - r.lastStopLap;
    let stopsToGo: number | null = null;
    const lapsLeft = lapsLeftBy?.get(Number(r.k)) ?? null;
    const fuelLapsLeft = fuelLapsBy?.get(Number(r.k)) ?? lapsLeft;
    if (stintCap !== null && r.pace !== null && fuelLapsLeft !== null && !r.stopped) {
      // None when this stint, saving what it can from its stop (reachToFlag: timing can not show
      // fuel saved, so the stint is judged whole), reaches the flag; else enough full stints that
      // the last, saving what it can, does.
      stopsToGo = stopsFor(stintCap, r.stintFuel, fuelLapsLeft);
    }
    let likely: ReturnType<typeof stopsWithCautions> | null = null;
    if (cautionStops && stopsToGo !== null && stintCap !== null && stintLength !== null && r.pace !== null && lapsLeft !== null && timeToGoS !== null) {
      // Under a caution now: has it stopped in this one already (its last stop after the caution's first lap)?
      const laps = r.car.laps;
      let yellowRun = 0;
      for (let j = laps.length - 1; j >= 0 && (fcy.has(laps[j].l) || (!!laps[j].flag && !isGreenFlag(laps[j].flag))); j--) yellowRun++;
      const underCaution = isCaution(flag);
      likely = stopsWithCautions({
        cap: stintCap,
        classStint: stintLength,
        stintFuel: r.stintFuel,
        lapsLeft,
        lapS: r.pace,
        timeToGoS,
        rates: cautionRates,
        outlook,
        underCaution,
        cautionShort: rcPits ? rcPits.short : null,
        stoppedThisCaution: underCaution && r.lastStopLap > 0 && r.lastStopLap > r.lapsDone - yellowRun,
        seed: Number(r.k) * 7919 + r.lapsDone * 31 + Math.round(r.stintFuel * 2),
      });
    }
    return {
      num: Number(r.k),
      cls,
      team: r.car.team,
      posClass: i + 1,
      laps: r.lapsDone,
      driver: r.driver,
      pace: r.pace,
      basePace: r.basePace,
      stops: stopsBy.get(r.k) ?? [],
      nextStopS: nextStopSFor(stopsBy.get(r.k) ?? []),
      lastStopLap: r.lastStopLap,
      stintLaps,
      stintFuel: r.stintFuel,
      stintCautionLaps: r.stintCautionLaps,
      longestStint,
      stintCap,
      lapsLeft,
      fuelLapsLeft,
      stopsToGo,
      stopsLikely: likely?.likely ?? null,
      stopsChance: likely?.chance ?? null,
      stopsUnderCaution: likely?.underCaution ?? null,
      gapS: i === 0 ? 0 : gapOf(r),
      netGapS: null,
      netPos: null,
      projGapS: null,
      projPos: null,
      stopped: r.stopped,
    };
  });

  // The last hour in IMSA: the stops still made follow the car's stop count against its class's
  // (lateStopsToGo), the fuel count alone over-counting them there.
  if (cautionRates.lateStopsByClass && timeToGoS !== null && timeToGoS < LAST_PHASE_S) {
    const made = (c: CarStrategy) => c.stops.filter((x) => x.kind !== "drive_through").length;
    const counts = cars.filter((c) => !c.stopped).map(made).sort((a, b) => a - b);
    const classMedian = counts.length ? (counts.length % 2 ? counts[(counts.length - 1) / 2] : (counts[counts.length / 2 - 1] + counts[counts.length / 2]) / 2) : 0;
    const leaderNowS = leadKey ? Math.max(0, ...(clock.get(leadKey)?.values() ?? [])) : 0;
    const sprintRace = timeToGoS + leaderNowS < SPRINT_S;
    for (const c of cars) {
      if (c.stopped) continue;
      const late = lateStopsToGo(c.stopsToGo, made(c) - classMedian, timeToGoS, sprintRace);
      if (late !== null) c.stopsToGo = late;
    }
  }

  // Net and projected order: cars still running, with stops to go known.
  // What a stop takes for its fuel: the fuel-free share fitted from the class's splashes (the fuel
  // put in: what it ran on after, less what was left in the tank), else SPLASH_FIXED.
  let fuelStop: FuelStop | null = null;
  if (fuelOnlyS !== null) {
    const fits: number[] = [];
    for (const r of rows) {
      const c = cars.find((x) => x.num === Number(r.k))!;
      if (c.stintCap === null) continue;
      for (const sp of r.splashes) {
        const after = sp.fuelAfter ?? (c.lapsLeft !== null ? r.stintFuel + c.lapsLeft : null);
        if (after === null) continue;
        const put = Math.max(1, after - Math.max(0, c.stintCap - sp.fuelBefore));
        const share = put / c.stintCap;
        if (share >= 0.8) continue;
        fits.push(Math.min(0.9, Math.max(0.3, (sp.lostS / fuelOnlyS - share) / (1 - share))));
      }
    }
    const fit = median(fits);
    // In a sprint the team fills the car: the last stop costs the whole fuel-only stop, however
    // little fuel the car needs to the flag (Road Atlanta 2026, #67: sized to 11.5 laps it was
    // charged 41 s and lost 62; the class's fuel-only stop read 57).
    fuelStop = sprint ? { fullS: fuelOnlyS, fixed: 1, fitted: false } : { fullS: fuelOnlyS, fixed: fit ?? SPLASH_FIXED, fitted: fit !== null };
  }

  let projectionWeight: number | null = null;
  if (greenStopS !== null) {
    const running = cars.filter((c) => !c.stopped && c.stopsToGo !== null);
    // The stops each still owes, the last for the fuel it needs (owedStopsS), against the leader's.
    const leadOwed = owedStopsS(cars[0], cars[0].nextStopS ?? greenStopS, fuelStop, cautionStopS);
    for (const c of running) c.netGapS = c.gapS + owedStopsS(c, c.nextStopS ?? greenStopS, fuelStop, cautionStopS) - leadOwed;
    const base = Math.min(...running.map((c) => c.netGapS!));
    for (const c of running) c.netGapS = c.netGapS! - base;
    // Cars that have stopped and are ahead in class keep their place (stoppedAhead).
    [...running].sort((a, b) => a.netGapS! - b.netGapS!).forEach((c, i) => (c.netPos = i + 1 + stoppedAhead(cars, c, false)));
    const lapsLeft = cars[0].lapsLeft;
    if (lapsLeft !== null && refPace) {
      // Each car's pace to the flag, and the time its fuel saving loses (reachToFlag).
      // A car saving fuel: its previous stint's pace and the saving's cost (its recent laps may be
      // slower for the saving already: counted once).
      // Taken in part far from the flag (projectionShare): the stops still owed and the paces to the
      // flag are estimates, and in full they moved the order further from the finish than today's
      // gaps did.
      const w = projectionShare(timeToGoS, cautionRates.projectionNearShare ?? 1);
      projectionWeight = w;
      for (const c of running) {
        const r = reachToFlag(c);
        const pace = r && r.save > 0 ? r.baseS : c.pace;
        const paceS = (pace !== null ? (pace - refPace) * lapsLeft : 0) + (r?.costS ?? 0);
        c.projGapS = c.gapS + w * (c.netGapS! - c.gapS + paceS);
      }
      const pb = Math.min(...running.map((c) => c.projGapS!));
      for (const c of running) c.projGapS = c.projGapS! - pb;
      [...running].sort((a, b) => a.projGapS! - b.projGapS!).forEach((c, i) => (c.projPos = i + 1 + stoppedAhead(cars, c, true)));
    }
  }

  const s: LiveStrategy = {
    cls,
    cars,
    greenStopS,
    cautionStopS,
    cautionMeasured: measuredCaution !== null,
    stopPolicy: cautionRates.stopPolicy ?? WRL_CAUTION_STOPS,
    projectionWeight,
    // Race control's calls first; without them IMSA's timing rule for a short yellow (pitLaneState).
    pitLaneClosed: rcPits ? !rcPits.open : !!pitLaneState(data, cls, caution, timeToGoS, cautionRates, outlook)?.short,
    pitLaneShort: rcPits ? rcPits.short : caution && !!cautionRates.shortCautionPitsClosed,
    pitLaneFromRc: rcPits !== null,
    cautionSoFarS: caution && outlook.cautionStartS !== null ? Math.max(0, outlook.nowS - outlook.cautionStartS) : null,
    cautionLengths: cautionRates,
    cautionOdds: cautionChance(cautionRates, outlook, timeToGoS),
    cautionsSoFar: { count: outlook.count, yellowS: outlook.yellowS },
    fuelStop,
    stintLength,
    stintLengthFrom,
    timeToGoS,
    caution,
    focus: null,
  };
  // Not yet in its class's table (the start: its first lap not run while others' are): no focus card.
  const fc = cars.find((c) => c.num === focusNum);
  if (fc) s.focus = { car: fc, window: pitWindowFor(fc), pitNow: pitNowFor(s, fc) };
  return s;
}
